import {FlowsRepository} from "@app/netscope/model/flows-repository";
import {
  AppsGroups,
  FlowDetail,
  FlowMetric,
  FlowRessourceObject,
  GraphDataObject,
  IncludedAppsGroupBoxState,
  MemberRoles,
  ResolvedAppsGroupsInclusion,
  defaultDisplayMode
} from "@app/netscope/model/data-models";
import {
  includedAppsGroupKey,
  isAppsGroupFolded,
  memberVisibilityKey
} from "@app/netscope/netscope-common-flowview/netscope-common-flowview.component";
import {AppsGroupFlowsStatus, FlowViewHost} from "@app/netscope/netscope-common-flowview/flowview-host";
import {
  ClustersViewerRendererSigmaJS
} from "@app/netscope/netscope-common-flowview/renderers/ClustersViewerRendererSigmaJS";

import {dia, shapes} from '@joint/core';
import { layout, EdgeRoutingMode, LayerDirectionEnum } from '@joint/layout-msagl';
import {AngularJointJsComponent, createAngularElementView} from "@app/netscope/netscope-common-flowview/jointjs-angular/angular-element-view/angular-element-view.component";
import * as joint from "@joint/core";
import {OptimizedAppsGroupsRouter} from "@app/netscope/netscope-common-flowview/misc/OptimizedAppsGroupsRouter";
import {AvoidRouter} from "@app/netscope/netscope-common-flowview/misc/AvoidRouter";
import {FlatAppsGroupsRouter} from "@app/netscope/netscope-common-flowview/misc/FlatAppsGroupsRouter";
import {NetscopeUtils} from "@app/netscope/netscope-utils";
import {
  FlowsDescriptionsClassification,
  LinkCategory
} from "@app/netscope/netscope-common-flowview/misc/FlowsDescriptionsClassification";
import {
  AppsGroupsExportFormat,
  AppsGroupsGraphExport
} from "@app/netscope/netscope-common-flowview/misc/AppsGroupsGraphExport";
import {
  EXTERNAL_IP_UUID_PREFIX,
  memberNetwork,
  resourceUuidLabel,
  uuidAddress
} from "@app/netscope/model/ip-addresses";


enum AppsGroupsRendererSimplificationLevels {
    noOptimizations = 0,
    groupClients = 1,
    groupServers = 2,
    groupEverything = 3,
}

class CellGenerationResult {
    allCells: dia.Cell[];
    nodesCells: dia.Cell[];
    subNodesCells: dia.Cell[];
    linksCells: dia.Cell[];
    subLinksCells: dia.Cell[];
}

function updateGraph(graph: dia.Graph, newCells: dia.Cell[]) {
  const existingCells = graph.getCells();
  const existingMap = new Map<string, dia.Cell>();
  existingCells.forEach(cell => existingMap.set(cell.id as string, cell));

  const newMap = new Map<string, dia.Cell>();
  newCells.forEach(cellJSON => newMap.set(cellJSON.id as string, cellJSON));

  // Build a parent lookup from the new state
  const newParentMap = new Map<string, string>();
  newCells.forEach(cellJSON => {
    const id = cellJSON.id as string;
    let parent = cellJSON.parent();
    // let embeds = cellJSON.embed();
    if (parent) {
      newParentMap.set(id, parent);
    }
    // if (cellJSON.embeds && cellJSON.embeds.length > 0) {
    //   newEmbedsMap.set(id, cellJSON.embeds as string[]);
    // }
  });

  // 1. Remove cells that no longer exist
  const toRemove: dia.Cell[] = [];
  existingMap.forEach((cell, id) => {
    if (!newMap.has(id)) {
      toRemove.push(cell);
    }
  });
  graph.removeCells(toRemove);

  // 2. Add new cells that don't exist yet
  const toAdd: dia.Cell[] = [];
  newMap.forEach((cellJSON, id) => {
    if (!existingMap.has(id)) {
      let parentCell = graph.getCell(cellJSON.parent());
      if (parentCell) {
        let parentPosition = parentCell.position();

        let existingSiblingsCells = parentCell.getEmbeddedCells()
        let maxY = parentPosition.y + 80;
        for (let cell of existingSiblingsCells) {
          // @ts-ignore
          let posY = cell.position().y + cell.size().height + 10;
          if (posY > maxY) {
            maxY = posY;
          }
        }
        // @ts-ignore
        cellJSON.position(parentPosition.x + 10, maxY);
      }

      toAdd.push(cellJSON);
    }
  });
  graph.addCells(toAdd);

  // 3. Update existing cells that are in both
  newMap.forEach((cellJSON, id) => {
    const existing = existingMap.get(id);
    if (!existing) return; // was just added above

    // // @ts-ignore
    // if (opts.updateAttributes && cellJSON.attrs) {
    //   // @ts-ignore
    //   existing.attr(cellJSON.attr());
    // }

    // @ts-ignore
    // if (opts.updatePositions && cellJSON.position) {
    // // @ts-ignore
    //   let newPosition = cellJSON.position();
    // // @ts-ignore
    //   existing.position(newPosition.x, newPosition.y);
    // }

    // @ts-ignore
    if (cellJSON.size) {
      // @ts-ignore
      let newSize = cellJSON.size();
      // @ts-ignore
      existing.resize(newSize.width, newSize.height);
    }

    // // @ts-ignore
    // if (opts.updateData && cellJSON.data) {
    //   // @ts-ignore
    //   existing.set('data', cellJSON.data);
    // }

    // Update link vertices/source/target if it's a link
    if (existing.isLink()) {
      // @ts-ignore
      let source = cellJSON.source();
      // @ts-ignore
      let target = cellJSON.target();
      // @ts-ignore
      let vertices = cellJSON.vertices();
      // @ts-ignore
      if (source) {
        // @ts-ignore
        existing.source(source);
      }
      // @ts-ignore
      if (target){
        // @ts-ignore
        existing.target(target);
      }
      // @ts-ignore
      if (vertices){
        // @ts-ignore
        existing.vertices(vertices);
      }
    }
  });

  // Fix Parents
  graph.getCells().map((cell) => {
    let parentCellId = cell.parent();
    if (parentCellId != undefined) {
      let parentCell = graph.getCell(parentCellId);
      parentCell.embed(cell);
    }
  })
}


export class GraphOptimizer {
  constructor() {
  }

  optimizeGraph = (graph: joint.dia.Graph): joint.dia.Graph => {
    for (let link of graph.getLinks()) {
      // @ts-ignore
      let sublinks: dia.Link[] = link.attributes.sublinks;
      console.log(sublinks);
    }
    return graph;
  }
}

// Gap between the links joining the same two nodes
const PARALLEL_LINKS_SPACING = 8;

/**
 * Wraps a router so that the links joining the same two nodes do not lie on each other. The bundled routers give
 * such links the very same route, as they route a pair of nodes once: each link is shifted aside of it by its
 * 'parallelOffset' (see spreadParallelLinks), its two ends included, the part inside the nodes being hidden.
 */
function parallelRoute(router: (vertices, options, linkView: dia.LinkView) => any[]) {
  return (vertices, options, linkView: dia.LinkView) => {
    let route = router(vertices, options, linkView) ?? [];
    let offset: number = linkView.model.get("parallelOffset") ?? 0;

    let source = linkView.model.getSourceElement();
    let target = linkView.model.getTargetElement();

    if (offset == 0 || source == undefined || target == undefined) {
      return route;
    }

    let points = [source.getBBox().center(), ...route, target.getBBox().center()]
      .map((point) => new joint.g.Point(point.x, point.y));

    return offsetPolyline(points, offset);
  };
}

// A polyline shifted aside by a distance, each segment keeping its direction
function offsetPolyline(points: joint.g.Point[], offset: number): {x: number, y: number}[] {
  let segments: joint.g.Line[] = [];

  for (let i = 0; i < points.length - 1; i++) {
    let a = points[i];
    let b = points[i + 1];
    let length = a.distance(b);
    if (length == 0) {
      continue;
    }
    let nx = -(b.y - a.y) / length;
    let ny = (b.x - a.x) / length;
    segments.push(new joint.g.Line(
      new joint.g.Point(a.x + nx * offset, a.y + ny * offset),
      new joint.g.Point(b.x + nx * offset, b.y + ny * offset)));
  }

  if (segments.length == 0) {
    return points.map((point) => ({x: point.x, y: point.y}));
  }

  let result: {x: number, y: number}[] = [segments[0].start];
  for (let i = 0; i < segments.length - 1; i++) {
    // Two consecutive segments meet where their shifted lines cross, or at the end of the first when parallel
    result.push(linesCrossing(segments[i], segments[i + 1]) ?? segments[i].end);
  }
  result.push(segments[segments.length - 1].end);

  return result.map((point) => ({x: point.x, y: point.y}));
}

// Point where the lines carrying two segments cross, beyond the segments if need be, or undefined when parallel
function linesCrossing(a: joint.g.Line, b: joint.g.Line): {x: number, y: number} {
  let d1x = a.end.x - a.start.x, d1y = a.end.y - a.start.y;
  let d2x = b.end.x - b.start.x, d2y = b.end.y - b.start.y;
  let denominator = d1x * d2y - d1y * d2x;

  if (Math.abs(denominator) < 1e-9) {
    return undefined;
  }

  let t = ((b.start.x - a.start.x) * d2y - (b.start.y - a.start.y) * d2x) / denominator;
  return {x: a.start.x + t * d1x, y: a.start.y + t * d1y};
}

export class AppGroupsVisualisationData {
      graph: GraphDataObject;
      appGroupsSizes: Map<string, number>
}

export class AppGroupsServersClientsRelation {
  appGroupUuid: string;
  serversRelations: Map<string, ClientServerRelation[]> = new Map();
}

export class ClientServerRelation {
  clientUuid: string;
  serverUuid: string;
  clientAppGroupUuid: string;
  serverAppGroupUuid: string;
}

/**
 * Box drawn inside an apps group for an apps group it includes. Closed, it stands for all of the members that
 * group brought in; opened, it holds them the way the including group holds its own members, and the groups it
 * includes in turn as boxes of their own, at any depth.
 */
export class IncludedAppsGroupBox {
  cellId: string;
  // Key of the box among the boxes of the including apps group: the keys of the inclusions from the top one
  // down to this one (see includedAppsGroupKey), joined by '>'
  key: string;
  name: string;
  // File holding the apps group the box stands for
  file: string;
  expanded: boolean;
  includingAppsGroup: AppsGroups;
  // Box this one is drawn in, none when it is drawn right in the including apps group
  parent: IncludedAppsGroupBox;
  childBoxes: IncludedAppsGroupBox[] = [];
  memberUuids: Set<string> = new Set();
  // Members as the included group lists them, its networks included: closing the box folds these as well, as
  // the resources a network gathers follow the display choice made on the network
  declaredMemberUuids: Set<string> = new Set();

  // Members held by the box itself rather than by the boxes inside of it
  ownUuids = (uuids: (box: IncludedAppsGroupBox) => Set<string>): string[] => {
    let inChildren = new Set(this.childBoxes.flatMap((child) => Array.from(uuids(child))));
    return Array.from(uuids(this)).filter((uuid) => !inChildren.has(uuid));
  }

  toState(): IncludedAppsGroupBoxState {
    return {
      key: this.key,
      includingAppsGroupName: this.includingAppsGroup.name,
      expanded: this.expanded,
      memberUuids: Array.from(this.memberUuids),
      declaredMemberUuids: Array.from(this.declaredMemberUuids),
      ownMemberUuids: this.ownUuids((box) => box.memberUuids),
      ownDeclaredMemberUuids: this.ownUuids((box) => box.declaredMemberUuids),
      name: this.name,
      file: this.file,
      includedCount: this.childBoxes.length,
    };
  }
}

export class SimpleSourceWithId {
  id: string;
  appGroupId?: string;
}

export class LinkAttributes {
  type: string;
  attrs: {
    model: {
      source: FlowRessourceObject;
      target: FlowRessourceObject;
      flow: FlowDetail;
      appsGroupsIds: {
        source: string;
        target: string;
      }
    }
  }
  source: SimpleSourceWithId;
  target: SimpleSourceWithId;
  id: string;
  connector: {
    name: string;
  };
  visibilityLevel: string;
  sublinks: { attributes: LinkAttributes }[];
  namesKey: string;
}

export class HyperLink extends joint.dia.Link {
  constructor(private link: joint.dia.Link, private subLinks: joint.dia.Link[], private simplificationLevel: AppsGroupsRendererSimplificationLevels, private visualisationData: AppGroupsVisualisationData, private memberEndpoint: (appGroupUuid: string, memberUuid: string) => string, private showsInternalFlows: (appGroupUuid: string) => boolean, private classification?: FlowsDescriptionsClassification) {
    super();
  }

  getDisplayedLinks(): joint.dia.Link[] {

    // @ts-ignore
    let linkAttributes: LinkAttributes = this.link.attributes;

    let result: joint.dia.Link[] = [];

    // @ts-ignore
    const defaultMarker = shapes.standard.Link.prototype.defaults.attrs.line.targetMarker;
    let sourceMarker = {...defaultMarker}

    let newLinksMap: Map<string, joint.dia.Link> = new Map();

    for (let sublink of this.subLinks) {
      // @ts-ignore
      let sublinkAttributes: LinkAttributes = sublink.attributes;
      let sourceKey = sublinkAttributes.source.id;
      let targetKey = sublinkAttributes.target.id;

      // let srcAppGroupUuid: string = sourceKey.split("->")[0];
      // let dstAppGroupUuid: string = targetKey.split("->")[0];

      let srcAppGroupUuid = sublinkAttributes.attrs.model.appsGroupsIds.source;
      let dstAppGroupUuid = sublinkAttributes.attrs.model.appsGroupsIds.target;

      let sourceMemberUuid = sublinkAttributes.attrs.model.source.uuid;
      let targetMemberUuid = sublinkAttributes.attrs.model.target.uuid;

      // A member folded into its apps group is not an endpoint of its own: the flow is then drawn from the node
      // of the apps group, which is what the 'summarized' mode does for every member of a group, or from the
      // box of the included apps group it belongs to.
      sourceKey = this.memberEndpoint(srcAppGroupUuid, sourceMemberUuid);
      targetKey = this.memberEndpoint(dstAppGroupUuid, targetMemberUuid);

      // Both ends are in the same apps group. Such a flow is only drawn for the groups showing their internal
      // traffic, and only between two ends shown inside the group: a folded member stands for the node of the
      // group itself, so the link would tie the group to something it already contains.
      if (srcAppGroupUuid == dstAppGroupUuid) {
        if (!this.showsInternalFlows(srcAppGroupUuid)) {
          continue;
        }
        if (sourceKey == srcAppGroupUuid || targetKey == dstAppGroupUuid) {
          continue;
        }
      }

      // A resource talking to itself, or both ends folded into the same node: that node already stands
      // for the flow, and a link from it to itself would tell nothing more.
      if (sourceKey == targetKey) {
        continue;
      }

      if (sublinkAttributes.attrs.model.flow.metrics.size == 0) {
        continue;
      }

      // The displayed links have an arrow at both ends, and the side panel lists the flows of both directions:
      // one link per pair of ends, whichever way its flows go, so that the flows of both directions (or the
      // links between the same two groups in both directions) are not drawn as several identical links
      let pairKey = [sourceKey, targetKey].sort().join("__");

      // The flows descriptions split the link, one per category of the flows it carries: a flow in several
      // categories (matching several descriptions) is carried by each of their links
      let categories: LinkCategory[] = this.classification != undefined
        ? this.classification.categoriesOf(sublinkAttributes.attrs.model.source.uuid, sublinkAttributes.attrs.model.target.uuid)
        : [undefined];

      for (let category of categories) {
        let flowKey = category != undefined ? `${pairKey}__${category.key}` : pairKey;

        if (!newLinksMap.has(flowKey)) {
          let newLink = new shapes.standard.Link({
            id: flowKey,
            // @ts-ignore
            source: {id: sourceKey, appGroupId: srcAppGroupUuid},
            // @ts-ignore
            target: {id: targetKey, appGroupId: dstAppGroupUuid},
            connector: {name: 'rounded'},
            visibilityLevel: "high",
            internal: false,
            parents: {
              source: linkAttributes.attrs.model.source,
              target: linkAttributes.attrs.model.target,
            },
            sublinks: [],
            category: category,
            attrs: {
              line: {
                stroke: category?.color ?? 'rgb(82 94 94)',
                strokeWidth: 3,
                strokeDasharray: category?.dashed ? '8 5' : undefined,
                sourceMarker: sourceMarker,
              },
              model: {},
            }
          });

          if (category?.label != undefined) {
            newLink.appendLabel({
              attrs: {
                text: {text: category.label, fill: category.color, fontSize: 11, fontWeight: 'bold'},
                rect: {fill: '#ffffff', stroke: category.color, strokeWidth: 1, rx: 3, ry: 3},
              },
            });
          }

          newLinksMap.set(flowKey, newLink);
        }
      }
    }

    result = Array.from(newLinksMap.values());

    console.log("plop");

    return result;
  }
}


export class AppsGroupsRendererJointJs extends ClustersViewerRendererSigmaJS {
  graphData: GraphDataObject;

  // Which flows descriptions match the flows of every pair of resources, when the links take them into account
  classification: FlowsDescriptionsClassification;

  // Name of every apps group, by uuid: the links only carry uuids, the display choices are held by name
  appGroupNamesByUuid: Map<string, string> = new Map();

  // Roles played by the members in the flows that are drawn, rebuilt at every computeVisualizationData
  memberRoles: Map<string, MemberRoles> = new Map();
  // Network member each resource was gathered by, so that the resources of a network follow its display
  // choices: a network stands for addresses the panel cannot know, it only knows the network itself.
  networkMemberByResourceUuid: Map<string, string> = new Map();
  // The same, by uuid of the apps group holding the network: a resource only follows the display choice made
  // on a network in the group drawing it through this network. A network as wide as '10.0.0.0/16' would
  // otherwise open every box of every other group as soon as it is shown.
  networkMembersByAppGroupUuid: Map<string, Map<string, string>> = new Map();
  // Members of every apps group as they were drawn, the networks resolved into the resources they gather:
  // the panel reads them back to tell how many resources a network ended up standing for.
  membersByAppsGroupName: Map<string, {uuid: string}[]> = new Map();
  // Boxes of the apps groups included in others, by uuid of the including group, a box before the ones inside
  // of it, and the boxes each member of an including group was brought in by, from the outermost one to the
  // innermost one. A member the group lists itself is in no box.
  includedBoxesByAppGroupUuid: Map<string, IncludedAppsGroupBox[]> = new Map();
  includedBoxesByMember: Map<string, Map<string, IncludedAppsGroupBox[]>> = new Map();

  // Can be: "optimized" | "flat" | "libavoid" | "hybrid"
  routerAlgorithm: string = "hybrid";
  // Can be: "full" | "hybrid" | "limited"
  mode: string = "full";
  avoidRouter: AvoidRouter;

  linksShouldBeUpdated = true;
  updateInterval;
  // Number of the current rendering, changed by every rendering and destruction: a rendering still waiting for
  // its data stops there once another one started or the renderer was destroyed, rather than drawing into the
  // element the next rendering draws into
  renderingNumber = 0;
  // Flows and their classification as last loaded, and what for (see loadedDataKey): folding or unfolding an
  // apps group draws the same flows again, which are then not loaded a second time
  private loadedData: {
    key: string,
    flowsRepository: FlowsRepository,
    graphData: GraphDataObject,
    classification: FlowsDescriptionsClassification
  };

  allResourcesArray: FlowRessourceObject[] = [];

  graph: dia.Graph;
  paper: dia.Paper;

  flowMap: Map<string, FlowDetail[]> = new Map<string, FlowDetail[]>();
  nodeMap: Map<string, FlowRessourceObject> = new Map();

  fitParentNodeWithChildren(c, graph) {

    // @ts-ignore
    if (c.size == undefined) {
      // Can be a link
      return;
    }

    let allXValues = [];
    let allYValues = [];

    let embeds = c.attributes["embeds"];
    if (embeds == undefined) {
      embeds = []
    }

    if (embeds.length > 0) {
      for (let childUuid of embeds) {
        let child = graph.getCell(childUuid);

        // The box of an included apps group holds members of its own: it is fitted to them first
        if ((child.attributes["embeds"] ?? []).length > 0) {
          this.fitParentNodeWithChildren(child, graph);
        }

        let childPositions: {
          x: number;
          y: number;
          // @ts-ignore
        } = child.position();
        let childSizes: {
          width: number;
          height: number;
          // @ts-ignore
        } = child.size();

        if (childSizes.height == -Infinity || childSizes.height == Infinity) {
          childSizes.height = 0;
        }

        if (childSizes.width == Infinity || childSizes.width == -Infinity) {
          childSizes.width = 0;
        }

        let childMinX = childPositions.x;
        let childMinY = childPositions.y;

        let childMaxX = childPositions.x + childSizes.width;
        let childMaxY = childPositions.y + childSizes.height;

        allXValues.push(...[childMinX, childMaxX]);
        allYValues.push(...[childMinY, childMaxY]);
      }

      let minX = Math.min(...allXValues);
      let maxX = Math.max(...allXValues);

      let minY = Math.min(...allYValues);
      let maxY = Math.max(...allYValues);

      // update position and size
      let newHeight = maxY - minY;
      let newWidth = maxX - minX;

      // Margin, as parent node have label, and children must be offset vertically to not overlap parent label. A
      // group no flow reaches tells so under its name, which takes a line more.
      let yOffset = 15;
      let flowsStatus = this.flowComponent?.appsGroupsFlowsStatus?.get(c.id?.toString());
      if (c.attributes.data?.type == "parent" && flowsStatus != undefined && flowsStatus != "flows") {
        yOffset += 18;
      }

      // Hack to ensure that the text of the parent node is not outside the box.
      // TODO: improve this
      let minWidth = c.attributes.data.appGroup.name.length * 12 + 10;

      // @ts-ignore
      c.position(minX - 10, minY - 10 - (yOffset + 20));

      // @ts-ignore
      c.resize(Math.max(newWidth + 20, minWidth), newHeight + 40 + yOffset);
    }
  }

  /**
   * Takes the flows another renderer of the same view loaded, which a new rendering then uses as long as they are
   * still the ones to draw: a renderer is made anew when the apps groups or the options change, not the flows.
   */
  takeLoadedDataFrom = (renderer: unknown) => {
    if (renderer instanceof AppsGroupsRendererJointJs) {
      this.loadedData = renderer.loadedData;
    }
  }

  /**
   * What the flows drawn and their classification depend on, besides the flows repository itself: its filters,
   * the files of the period it reads, and how the links take the flows descriptions into account.
   */
  private loadedDataKey = (flowComponent: FlowViewHost): string => {
    let flowsRepository = flowComponent.flowsRepository;
    return JSON.stringify([flowsRepository?.generateWhereClause(), flowsRepository?.flows_files,
                           flowsRepository?.aggregated_flows_files, flowComponent.appsGroupsLinksMode]);
  }

  /**
   * Gives the flows table the flows of the apps groups that are drawn: the ones between two of their members,
   * networks resolved into the resources they gather, whether these are in two groups or in the same one.
   */
  setDatatableFlows = () => {
    let uuids: Set<string> = new Set();
    for (let members of this.membersByAppsGroupName.values()) {
      members.forEach((member) => uuids.add(member.uuid));
    }
    let list = Array.from(uuids).map((uuid) => `'${uuid.replace(/'/g, "''")}'`).join(", ");
    let membersClause = list.length > 0
      ? `src_resource_uuid in (${list}) and dst_resource_uuid in (${list})`
      : "1 = 0";

    this.flowComponent.datatableTabFlowsRepository = this.flowComponent.flowsRepository.clone();
    this.flowComponent.datatableTabFlowsRepository.setWhereClause(
      ` src_resource_type != 'internet_ip' and dst_resource_type != 'internet_ip' and ${membersClause} `);
    this.flowComponent.datatableTabFlowsRepository.showFirstPage();
  }

  /**
   * Whether the flows exchanged between the members of an apps group are drawn. They are hidden by default:
   * the toggle of the apps groups panel turns them on for a single group, and the option of the toolbar turns
   * them on for all of them at once.
   */
  showsInternalFlows = (appGroupUuid: string): boolean => {
    if (this.flowComponent.showInternalFlowsAppsGroups) {
      return true;
    }

    let appGroupName = this.appGroupNamesByUuid.get(appGroupUuid);

    return this.flowComponent.appGroupStates.get(appGroupName)?.showInternalFlows ?? false;
  }

  /**
   * Whether an apps group is drawn as a single box: its included groups get no box and its members no node,
   * the flows of all of them ending on the box of the group.
   */
  isAppGroupFolded = (appGroupUuid: string): boolean => {
    let appGroupName = this.appGroupNamesByUuid.get(appGroupUuid);
    return appGroupName != undefined && isAppsGroupFolded(this.flowComponent, appGroupName);
  }

  /**
   * Whether a member of an apps group is drawn as a node of its own. Members are folded into the node of their
   * apps group by default: the display modes of the apps groups panel set this flag for a whole group at once,
   * and the user can then show or fold any member on its own from that same panel.
   */
  isMemberVisible = (uuid: string, appGroupUuid: string): boolean => {
    // A member is shown or folded in the group drawing it alone (see memberVisibilityKey)
    let appGroupName = this.appGroupNamesByUuid.get(appGroupUuid);
    let visibilityKey = memberVisibilityKey(appGroupName, uuid);
    if (this.flowComponent.vmVisibilityStates.has(visibilityKey)) {
      return this.flowComponent.vmVisibilityStates.get(visibilityKey);
    }

    // A resource gathered by a network follows the display choice made on that network, until the user makes
    // one on the resource itself: the panel knows the network, not the resources it ended up gathering
    let networkUuid = this.networkMembersByAppGroupUuid.get(appGroupUuid)?.get(uuid);
    if (networkUuid != undefined) {
      return this.flowComponent.vmVisibilityStates.get(memberVisibilityKey(appGroupName, networkUuid)) ?? false;
    }

    return false;
  }

  /**
   * Spreads the links joining the same two nodes, which the flows descriptions split a link into: each of them
   * is shifted aside of the route the pair of nodes was given (see parallelRoute), and its label is written at
   * its own place along it, so that the labels do not pile up on each other.
   */
  spreadParallelLinks = (links: dia.Cell[]) => {
    let linksByPair: Map<string, dia.Cell[]> = new Map();

    for (let link of links) {
      // @ts-ignore
      let pairKey = [link.attributes.source.id, link.attributes.target.id].sort().join("__");
      if (!linksByPair.has(pairKey)) {
        linksByPair.set(pairKey, []);
      }
      linksByPair.get(pairKey).push(link);
    }

    for (let pairLinks of linksByPair.values()) {
      pairLinks.forEach((link, index) => {
        let rank = index - (pairLinks.length - 1) / 2;
        link.set("parallelOffset", rank * PARALLEL_LINKS_SPACING);

        // @ts-ignore
        if ((link.labels?.() ?? []).length > 0) {
          let distance = Math.min(0.85, Math.max(0.15, 0.5 + rank * 0.15));
          // @ts-ignore
          link.label(0, {position: {distance: distance}});
        }
      });
    }
  }

  /**
   * Builds the boxes of the apps groups included in an apps group, and inside of them the boxes of the groups
   * they include in turn. A network brought in by an included group is resolved as the members of the group are,
   * so that the resources it gathers land in that same box.
   */
  registerIncludedBoxes = (appGroup: AppsGroups, graphData: GraphDataObject) => {
    // A folded group holds no box: what its included groups bring in is folded into it with the rest
    if (this.isAppGroupFolded(appGroup.uuid)) {
      this.includedBoxesByAppGroupUuid.set(appGroup.uuid, []);
      this.includedBoxesByMember.set(appGroup.uuid, new Map());
      return;
    }

    let expandedKeys = this.flowComponent.appGroupStates.get(appGroup.name)?.expandedIncludedAppsGroups;
    let hiddenKeys = this.flowComponent.appGroupStates.get(appGroup.name)?.hiddenIncludedAppsGroups;

    let boxes: IncludedAppsGroupBox[] = [];
    let boxesByMember: Map<string, IncludedAppsGroupBox[]> = new Map();

    let addBoxes = (inclusions: ResolvedAppsGroupsInclusion[], parent: IncludedAppsGroupBox) => {
      // A member brought in by two inclusions goes to the first of them
      let claimedUuids: Set<string> = new Set();

      inclusions.forEach((inclusion, index) => {
        // Folded into the including group from the panel: its members are drawn as the group's own ones
        if (parent == undefined && hiddenKeys?.has(includedAppsGroupKey(inclusion))) {
          return;
        }

        let box = new IncludedAppsGroupBox();
        // The id holds no '__', which the links use to separate their two ends
        box.cellId = parent == undefined ? `${appGroup.uuid}->included:${index}` : `${parent.cellId}:${index}`;
        box.key = parent == undefined
          ? includedAppsGroupKey(inclusion)
          : `${parent.key}>${includedAppsGroupKey(inclusion)}`;
        box.name = inclusion.name;
        // An inclusion naming no file points inside the file holding the group
        box.file = inclusion.file ?? parent?.file ?? this.flowComponent.appsGroupsMode;
        box.includingAppsGroup = appGroup;
        box.parent = parent;

        for (let member of this.resolveNetworkMembers(inclusion.members, graphData, appGroup.uuid)) {
          // A box only holds members of the box it is drawn in
          if (parent != undefined && !parent.memberUuids.has(member.uuid)) {
            continue;
          }
          if (!claimedUuids.has(member.uuid)) {
            claimedUuids.add(member.uuid);
            box.memberUuids.add(member.uuid);
            boxesByMember.set(member.uuid, (boxesByMember.get(member.uuid) ?? []).concat([box]));
          }
        }
        inclusion.members.forEach((member) => box.declaredMemberUuids.add(member.uuid));

        // A box is opened by the user, or as soon as one of its members is to be seen: a display mode of the
        // including group, or a member shown from the panel, would otherwise be hidden by the closed box
        box.expanded = (expandedKeys?.has(box.key) ?? false)
          || Array.from(box.memberUuids).some((uuid) => this.isMemberVisible(uuid, appGroup.uuid));

        boxes.push(box);
        parent?.childBoxes.push(box);
        addBoxes(inclusion.inclusions ?? [], box);
      });
    };
    addBoxes(appGroup.resolvedInclusions ?? [], undefined);

    this.includedBoxesByAppGroupUuid.set(appGroup.uuid, boxes);
    this.includedBoxesByMember.set(appGroup.uuid, boxesByMember);
  }

  /**
   * Node a member of an apps group is drawn on:
   *  - the apps group itself while it is folded,
   *  - the outermost closed box among the boxes of included apps groups it belongs to, whatever the member,
   *  - its own node when it is shown,
   *  - otherwise the node it is folded into: the innermost box of an included apps group it belongs to, or the
   *    apps group itself.
   */
  memberEndpoint = (appGroupUuid: string, memberUuid: string): string => {
    if (this.isAppGroupFolded(appGroupUuid)) {
      return appGroupUuid;
    }

    let boxes = this.includedBoxesByMember.get(appGroupUuid)?.get(memberUuid) ?? [];

    let closedBox = boxes.find((box) => !box.expanded);
    if (closedBox != undefined) {
      return closedBox.cellId;
    }

    if (this.isMemberVisible(memberUuid, appGroupUuid)) {
      return `${appGroupUuid}->${memberUuid}`;
    }

    return boxes.length > 0 ? boxes[boxes.length - 1].cellId : appGroupUuid;
  }

  /**
   * Members of an apps group, the networks among them being replaced by the resources they gather. A network
   * is not a resource of the flows: it stands for every address falling inside it, which is only known once
   * the flows are drawn, and is therefore resolved here rather than in the panel.
   *
   *  - a network typed in CIDR notation gathers the resources whose address falls inside it,
   *  - a configured external network gathers the addresses DC Netscope named after it, as it names each of
   *    them by the offset it has in that network ('vim.ExternalIpaddress:bureau:13').
   */
  resolveNetworkMembers = (members: {uuid: string}[], graphData: GraphDataObject,
                           appGroupUuid: string): {uuid: string}[] => {
    let resolvedMembers: {uuid: string}[] = [];

    for (let member of members ?? []) {
      let network = memberNetwork(member.uuid);

      // A configured external IP stands for a network as soon as DC Netscope named addresses after it, which
      // it does by appending their offset in it. An address inside such a network ('...:bureau:13') is a
      // member as any other, it is a single resource of the flows.
      let isConfiguredExternalIp = member.uuid != undefined
        && member.uuid.startsWith(EXTERNAL_IP_UUID_PREFIX)
        && member.uuid.split(":").length == 2;

      if (network == undefined && !isConfiguredExternalIp) {
        resolvedMembers.push(member);
        continue;
      }

      // A configured external IP is kept: it is the very resource the flows hold when it is a single address.
      // A network typed in CIDR notation is left out, no flow ever ends on it and it would be drawn as a node
      // nothing links to. The panel keeps showing it either way, it is what the apps groups file holds.
      if (isConfiguredExternalIp) {
        resolvedMembers.push(member);
      }

      let addressesPrefix = `${member.uuid}:`;
      for (let node of graphData.nodes) {
        let isGathered = network != undefined
          ? network.contains(node.address)
          : node.uuid != undefined && node.uuid.startsWith(addressesPrefix);

        if (!isGathered) {
          continue;
        }

        resolvedMembers.push({uuid: node.uuid});
        this.networkMemberByResourceUuid.set(node.uuid, member.uuid);
        if (!this.networkMembersByAppGroupUuid.has(appGroupUuid)) {
          this.networkMembersByAppGroupUuid.set(appGroupUuid, new Map());
        }
        this.networkMembersByAppGroupUuid.get(appGroupUuid).set(node.uuid, member.uuid);
      }
    }

    // A resource gathered by a network may also be a member of its own, and two networks may overlap
    let membersByUuid: Map<string, {uuid: string}> = new Map();
    for (let member of resolvedMembers) {
      if (!membersByUuid.has(member.uuid)) {
        membersByUuid.set(member.uuid, member);
      }
    }

    return Array.from(membersByUuid.values());
  }

  /**
   * Records the roles played by the two ends of a flow that is drawn: a resource is a server as soon as it
   * exposes the port of one of them, and a client as soon as it uses the port of another one.
   *
   * Only the flows that end up in the picture are taken into account. The display modes are presets over these
   * roles, and a member whose whole traffic was left out — it only talks inside its own apps group, or only to
   * resources belonging to no group — would otherwise be shown as a node nothing links to.
   */
  recordMemberRoles = (link: FlowDetail) => {
    let rolesOf = (uuid: string): MemberRoles => {
      if (!this.memberRoles.has(uuid)) {
        this.memberRoles.set(uuid, new MemberRoles());
      }
      return this.memberRoles.get(uuid);
    };

    for (let metric of link.metrics?.values() ?? []) {
      // 'direction' tells which end of the flow exposes its port: the destination when it is 0
      let sourceIsServer = metric.direction != 0;

      rolesOf(link.source.uuid)[sourceIsServer ? "server" : "client"] = true;
      rolesOf(link.target.uuid)[sourceIsServer ? "client" : "server"] = true;
    }
  }

  /**
   * Node of a member of an apps group. A member that exchanged nothing over the displayed period is not part
   * of the graph data: a node is built for it from the resources mapping, but only when the user asked to see
   * it, as it would otherwise be folded into its apps group anyway.
   */
  memberNode = (member: {uuid: string, name?: string}, appGroupUuid: string): FlowRessourceObject => {
    if (this.nodeMap.has(member.uuid)) {
      return this.nodeMap.get(member.uuid);
    }

    if (!this.isMemberVisible(member.uuid, appGroupUuid)) {
      return undefined;
    }

    let mappedResource = this.flowComponent.flowsRepository?.duckDbNodeMapping?.get(member.uuid);

    // A member that took part in no flow of the displayed period is in neither of them, and is named after
    // its uuid: an address or a network is read back from it rather than shown as 'vim.UnknownIpaddress:...'
    let node = new FlowRessourceObject();
    node.uuid = member.uuid;
    node.name = mappedResource?.name ?? member.name ?? resourceUuidLabel(member.uuid);
    node.type = mappedResource?.type ?? NetscopeUtils.detectType(member.uuid);
    node.address = mappedResource?.address ?? uuidAddress(member.uuid);

    this.nodeMap.set(member.uuid, node);

    return node;
  }

  computeVisualizationData(graphData: GraphDataObject, appsGroups: AppsGroups[], focusedAppsGroups: any[], directionalLinks: boolean): AppGroupsVisualisationData {
    let nodeUuidsToAppsGroupsMap: Map<string, Array<AppsGroups>> = new Map();

    this.memberRoles = new Map();
    this.networkMemberByResourceUuid = new Map();
    this.networkMembersByAppGroupUuid = new Map();
    this.membersByAppsGroupName = new Map();
    this.includedBoxesByAppGroupUuid = new Map();
    this.includedBoxesByMember = new Map();

    // The links only carry the uuids of the apps groups, while their display choices are held by name
    this.appGroupNamesByUuid = new Map();
    for (let appGroup of appsGroups) {
      this.appGroupNamesByUuid.set(appGroup.uuid, appGroup.name);
    }

    this.nodeMap = new Map();
    for (let node of graphData.nodes) {
      this.nodeMap.set(node.uuid, node);
    }

    let newNodes: Map<string, FlowRessourceObject> = new Map();
    let newLinks: FlowDetail[] = [];

    // First detect categories that should be ignored, as their tags will be displayed (prevent to present several
    // times the same flows)
    let setIgnoredAppsGroups = new Set<string>();
    for (let appGroup of appsGroups) {

      // Set displays members mode
      if (this.flowComponent.appGroupStates.has(appGroup.name)) {
        appGroup.mode = this.flowComponent.appGroupStates.get(appGroup.name).mode;
      }

      if (!appGroup.name.includes(".")) {
        continue;
      }

      let parts = appGroup.name.split(".");
      let currentParts = [];
      for (let i = 0; i < parts.length - 1; i++) {
        currentParts.push(parts[i]);
        let currentName = currentParts.join(".");
        setIgnoredAppsGroups.add(currentName);
      }
    }

    // Then the groups included by another group of the file that is drawn: they are drawn inside it, as the boxes
    // of its sub-groups, and drawn on their own as well their members would stand in two nodes and their flows be
    // drawn twice. A category hidden above does not hide the groups of its tags, they are drawn in its stead.
    let drawnAppsGroupsNames = new Set(appsGroups
      .map((appGroup) => appGroup.name)
      .filter((name) => !setIgnoredAppsGroups.has(name) && !this.flowComponent.hiddenAppsGroups.has(name)));
    for (let appGroup of appsGroups) {
      if (!drawnAppsGroupsNames.has(appGroup.name)) {
        continue;
      }
      for (let included of appGroup.included_apps_groups ?? []) {
        // An inclusion naming no file points inside the file holding the group
        let includedFile = included.file ?? this.flowComponent.appsGroupsMode;
        if (includedFile == this.flowComponent.appsGroupsMode && included.name != appGroup.name) {
          setIgnoredAppsGroups.add(included.name);
        }
      }
    }

    // Create a Map of new nodes, and ensure we can get the apps groups from a node
    for (let appGroup of appsGroups) {

      // Check if the appGroups should be ignored (i.e. appGroup is a category)
      if (setIgnoredAppsGroups.has(appGroup.name)) {
        continue;
      }

      // Apps groups hidden from the apps groups panel are not rendered: neither their node, nor the flows of
      // their members, which are dropped below as they end up without any source or target object
      if (this.flowComponent.hiddenAppsGroups.has(appGroup.name)) {
        continue;
      }

      // The networks held by the apps group stand for the resources falling inside them, which is only known
      // now that the flows are drawn
      let members = this.resolveNetworkMembers(appGroup.vms, graphData, appGroup.uuid);
      this.membersByAppsGroupName.set(appGroup.name, members);
      this.registerIncludedBoxes(appGroup, graphData);

      let subnodesMap: Map<string, FlowRessourceObject> = new Map();
      for (let vm of members) {
        // @ts-ignore
        subnodesMap.set(vm.uuid, vm);
      }

      let appGroupMode = appGroup.mode
      if (appGroupMode == undefined) {
        appGroupMode = defaultDisplayMode(appGroup);
      }

      // First ensure an app-group node is created
      let appGroupNode: FlowRessourceObject = {
        uuid: appGroup.uuid,
        name: appGroup.name,
        type: "appGroup",
        address: "0.0.0.0",
        nonInteractive: false,
        subnodes: members.map((member) => this.memberNode(member, appGroup.uuid)),
        subnodesMap: subnodesMap,
        appsGroupObject: appGroup,
        membersDisplayMode: appGroupMode
      };
      newNodes.set(appGroup.name, appGroupNode);
      // this.synchronizer.associateNode(appGroupNode, )

      // For each node of the app-group, ensure we can quickly find the apps-groups of the VMs
      for (let node of members) {
        if (!nodeUuidsToAppsGroupsMap.has(node.uuid)) {
          nodeUuidsToAppsGroupsMap.set(node.uuid, []);
        }
        nodeUuidsToAppsGroupsMap.get(node.uuid).push(appGroup);
        // Add the node : if not used, it will not appear
        if (this.nodeMap.has(node.uuid)) {
          newNodes.set(node.uuid, this.nodeMap.get(node.uuid));
        }
      }
    }

    let ignoredAppsGroups = focusedAppsGroups
      .map((appGroup) => appGroup.resource.uuid);

    let replacementFlowsMap: Map<string, FlowDetail> = new Map<string, FlowDetail>();

    for (let link of graphData.links) {

      let sourcesObjects: Array<FlowRessourceObject> = [];
      let targetObjects: Array<FlowRessourceObject> = [];

      // First, process the sources
      if (nodeUuidsToAppsGroupsMap.has(link.source.uuid)) {
        let correspondingAppsGroups = nodeUuidsToAppsGroupsMap.get(link.source.uuid);
        let correspondingAppsGroupsNodeObject = correspondingAppsGroups
          .map((appGroupObject) => newNodes.get(appGroupObject.name));
        let nonIgnoredAppsGroups = correspondingAppsGroupsNodeObject
          .filter((appgroup) => !ignoredAppsGroups.includes(appgroup.name));
        sourcesObjects = nonIgnoredAppsGroups;
        if (nonIgnoredAppsGroups.length < correspondingAppsGroupsNodeObject.length) {
          sourcesObjects.push(link.source);
        }
      }

      // Second, process the target
      if (nodeUuidsToAppsGroupsMap.has(link.target.uuid)) {
        let correspondingAppsGroups = nodeUuidsToAppsGroupsMap.get(link.target.uuid);
        let correspondingAppsGroupsNodeObject = correspondingAppsGroups
          .map((appGroupObject) => newNodes.get(appGroupObject.name))
        let nonIgnoredAppsGroups = correspondingAppsGroupsNodeObject
          .filter((appgroup) => !ignoredAppsGroups.includes(appgroup.name));
        targetObjects = nonIgnoredAppsGroups;
        if (nonIgnoredAppsGroups.length < correspondingAppsGroupsNodeObject.length) {
          targetObjects.push(link.target);
        }
      }

      // Third, replace the links if it relevant
      if (sourcesObjects.length == 0 || targetObjects.length == 0) {
        continue;
      }

      // Fourth, if an appgroup is focused, use the original node
      for (let newSourceObject of sourcesObjects) {
        for (let newTargetObject of targetObjects) {

          if (newSourceObject.uuid == newTargetObject.uuid && !this.showsInternalFlows(newSourceObject.uuid)) {
            continue;
          }

          // The flow made it to the picture: its two ends play their role in what the user actually sees
          this.recordMemberRoles(link);

          let linkSortedKey = [newSourceObject.uuid, newTargetObject.uuid].sort().join("__");
          let linkKey = `${newSourceObject.uuid}__${newTargetObject.uuid}`;

          if (!directionalLinks) {
            linkKey = linkSortedKey;
          }

          if (!replacementFlowsMap.has(linkKey)) {
            let newFlow = new FlowDetail();
            newFlow.source = newSourceObject;
            newFlow.target = newTargetObject;
            newFlow.metrics = new Map<number, FlowMetric>();
            newFlow.port = -1;
            newFlow.subflows = [];
            newFlow.exchanged_packets = 0;
            newFlow.exchanged_bytes = 0;
            // @ts-ignore
            newFlow.linkSortedKey = linkSortedKey;
            replacementFlowsMap.set(linkKey, newFlow);
            newLinks.push(newFlow);
          }

          let replacementFlow = replacementFlowsMap.get(linkKey);
          replacementFlow.subflows.push(link);

          for (let [metricPort, metric] of link.metrics) {

            if (!replacementFlow.metrics.has(metric.port)) {
              let newFlowMetric = new FlowMetric();
              newFlowMetric.port = metricPort;
              newFlowMetric.exchanged_bytes = 0;
              newFlowMetric.exchanged_packets = 0;
              newFlowMetric.direction = metric.direction;
              newFlowMetric.application_protocol = metric.application_protocol;
              newFlowMetric.transport_protocols = metric.transport_protocols;
              replacementFlow.metrics.set(metric.port, newFlowMetric);
            }

            // set exchanged_bytes
            let existingFlowMetric = replacementFlow.metrics.get(metric.port)
            existingFlowMetric.exchanged_bytes += metric.exchanged_bytes;
            existingFlowMetric.exchanged_packets += metric.exchanged_packets;
          }

          replacementFlow.exchanged_bytes += link.exchanged_bytes;
          replacementFlow.exchanged_packets += link.exchanged_packets;
        }
      }
    }

    let newNodesArray = [];
    let newLinksArray = [];

    for (let newNode of newNodes.values()) {
      newNodesArray.push(newNode);
    }

    for (let newLink of replacementFlowsMap.values()) {
      newLinksArray.push(newLink);
    }

    // Sort newLinksArray
    // @ts-ignore
    newLinksArray = newLinksArray.sort((a, b) => a.linkSortedKey.localeCompare(b.linkSortedKey));

    let newGraph: GraphDataObject = new GraphDataObject();
    newGraph.nodes = newNodesArray;
    newGraph.links = newLinksArray;

    // A network plays the roles of the resources it gathers: it is a single member for the panel, and the
    // display modes have to tell a network that answers as a server from one that took part in no flow
    for (let [resourceUuid, networkUuid] of this.networkMemberByResourceUuid) {
      let resourceRoles = this.memberRoles.get(resourceUuid);

      if (resourceRoles == undefined) {
        continue;
      }

      if (!this.memberRoles.has(networkUuid)) {
        this.memberRoles.set(networkUuid, new MemberRoles());
      }

      let networkRoles = this.memberRoles.get(networkUuid);
      networkRoles.server = networkRoles.server || resourceRoles.server;
      networkRoles.client = networkRoles.client || resourceRoles.client;
    }

    // Published once every drawn flow was seen: the panel turns these roles into its display modes
    this.flowComponent.memberRoles = this.memberRoles;

    let appGroupsSizes: Map<string, number> = new Map();
    for (let appGroup of appsGroups) {
      let appGroupMembersIndex: Map<string, {uuid: string}> = new Map();
      // The members resolved above, so that the resources gathered by a network are counted in
      for (let vm of this.membersByAppsGroupName.get(appGroup.name) ?? appGroup.vms) {
        appGroupMembersIndex.set(vm.uuid, vm);
      }
      let matchingNodes = newNodesArray.filter((n) => appGroupMembersIndex.has(n.uuid));
      appGroupsSizes.set(appGroup.uuid, matchingNodes.length);
    }

    return {
      graph: newGraph,
      appGroupsSizes: appGroupsSizes
    }
  }

  generateBigFakeGraphData = () => {
    let newAppsGroupsNodesArray = [];
    let newVmNodesArray = [];
    let newNodesArray = [];
    let newLinksArray = [];

    // let groupsCount = 10;
    // let vmsPerGroup = 30;
    // let totalLinks = 500;

    // let groupsCount = 10;
    // let vmsPerGroup = 30;
    // let totalLinks = 1000;

    // let groupsCount = 5;
    // let vmsPerGroup = 20;
    // let totalLinks = 100;

    // let groupsCount = 10;
    // let vmsPerGroup = 30;
    // let totalLinks = 5000;

    let groupsCount = 30;
    let vmsPerGroup = 100;
    let totalLinks = 30000;

    // let groupsCount = 3;
    // let vmsPerGroup = 8;
    // let totalLinks = 20;

    // let groupsCount = 2;
    // let vmsPerGroup = 4;
    // let totalLinks = 1;

    // let groupsCount = 1;
    // let vmsPerGroup = 2;
    // let totalLinks = 5;

    for (let groupNum = 0; groupNum < groupsCount; groupNum++) {
      let appGroupNode = new FlowRessourceObject();
      appGroupNode.name = "group_" + groupNum;
      appGroupNode.uuid = "vim.group:" + appGroupNode.name;
      appGroupNode.type = "appGroup";
      for (let vmNum = 0; vmNum < vmsPerGroup; vmNum++) {
        let vmNode = new FlowRessourceObject();
        vmNode.name = "vm_" + groupNum + "_" + vmNum;
        vmNode.uuid = "vim.vm:" + vmNode.name;
        vmNode.type = "vm";
        vmNode.parent = appGroupNode;

        appGroupNode.subnodes.push(vmNode);
        newNodesArray.push(vmNode);
        newVmNodesArray.push(vmNode);
      }
      newNodesArray.push(appGroupNode);
      newAppsGroupsNodesArray.push(appGroupNode);
    }

    const randomChoice = <T>(arr: T[]): T => arr[Math.floor(Math.random() * arr.length)];

    let parentFlowIndex = new Map<string, FlowDetail>();
    let linkIdIndex = new Map<string, string>();

    for (let numLink = 0; numLink < totalLinks; numLink += 1) {
    // while (newLinksArray.length < totalLinks) {
      let newFlowDetail = new FlowDetail();

      // newFlowDetail.source = randomChoice(newAppsGroupsNodesArray);
      newFlowDetail.source = randomChoice(newVmNodesArray);
      newFlowDetail.target = randomChoice(newVmNodesArray);

      let newFlowId = `${newFlowDetail.source.uuid}__${newFlowDetail.target.uuid}`;

      if (linkIdIndex.has(newFlowId)) {
        continue;
      }
      linkIdIndex.set(newFlowId, newFlowId);

      newFlowDetail.exchanged_bytes = 1024;
      newFlowDetail.exchanged_packets = 1024;

      if (newFlowDetail.source == newFlowDetail.target) {
        continue;
      }

      if (newFlowDetail.source.parent == undefined || newFlowDetail.target.parent == undefined) {
        let appGroupSource = newFlowDetail.source.parent == undefined ? newFlowDetail.source.uuid : newFlowDetail.source.parent.uuid
        let appGroupTarget = newFlowDetail.target.parent == undefined ? newFlowDetail.target.uuid : newFlowDetail.target.parent.uuid

        if (appGroupSource == appGroupTarget) {
          continue;
        }
      }

      // if (newFlowDetail.source.parent == undefined || newFlowDetail.target.parent == undefined) {
      //   continue
      // }

      let parentFlowSource = newFlowDetail.source.parent != undefined ? newFlowDetail.source.parent : newFlowDetail.source;
      let parentFlowTarget = newFlowDetail.target.parent != undefined ? newFlowDetail.target.parent : newFlowDetail.target;

      let parentFlowKey = `${parentFlowSource.uuid}__${parentFlowTarget.uuid}`;

      if (! parentFlowIndex.has(parentFlowKey)) {
        let parentFlow = new FlowDetail();

        parentFlow.source = parentFlowSource;
        parentFlow.target = parentFlowTarget;
        parentFlow.exchanged_bytes = 1024;
        parentFlow.exchanged_packets = 1024;
        parentFlow.subflows = [];

        parentFlowIndex.set(parentFlowKey, parentFlow);
        newLinksArray.push(parentFlow);
      }

      let parentFlow = parentFlowIndex.get(parentFlowKey);

      parentFlow.subflows.push(newFlowDetail);
      newLinksArray.push(newFlowDetail);
    }

    let newGraph: GraphDataObject = new GraphDataObject();
    newGraph.nodes = newNodesArray;
    newGraph.links = newLinksArray;

    return {
      graph: newGraph
    }
  }

  /**
   * Whether each apps group and each box of a group it includes exchanged flows (see AppsGroupFlowsStatus), from
   * the ids of the cells the links drawn end on. A box exchanged flows when a link ends on it, on one of its
   * members or on a box inside of it, and a group when a link ends on it or on anything drawn inside of it.
   */
  private computeFlowsStatus(appGroupNodes: FlowRessourceObject[], linkedIds: Set<string>): Map<string, AppsGroupFlowsStatus> {
    let statuses: Map<string, AppsGroupFlowsStatus> = new Map();
    let linkedIdsList = Array.from(linkedIds);
    let inventory = this.flowComponent.flowsRepository?.duckDbNodeMapping;

    for (let appGroupNode of appGroupNodes) {
      // A box comes after the ones holding it: going backwards, the boxes inside of a box are known before it
      let boxes = this.includedBoxesByAppGroupUuid.get(appGroupNode.uuid) ?? [];
      for (let box of [...boxes].reverse()) {
        let linked = linkedIds.has(box.cellId)
          || Array.from(box.memberUuids).some((uuid) => linkedIds.has(`${appGroupNode.uuid}->${uuid}`))
          || box.childBoxes.some((child) => statuses.get(child.cellId) == "flows");
        let empty = box.declaredMemberUuids.size == 0
          && box.childBoxes.every((child) => statuses.get(child.cellId) == "empty");
        statuses.set(box.cellId, linked ? "flows" : (empty ? "empty" : "silent"));
      }

      let prefix = `${appGroupNode.uuid}->`;
      if (linkedIds.has(appGroupNode.uuid) || linkedIdsList.some((id) => id.startsWith(prefix))) {
        statuses.set(appGroupNode.uuid, "flows");
        continue;
      }

      // The members of the groups it includes are members of its own as well
      let members = appGroupNode.appsGroupObject?.vms ?? [];
      if (members.length == 0) {
        statuses.set(appGroupNode.uuid, "empty");
        continue;
      }

      // Only virtual machines and hosts are looked for in the inventory: addresses and networks are not in it
      let missing = inventory != undefined && inventory.size > 0 && members.every((member) =>
        /(VirtualMachine|HostSystem)/.test(member.uuid ?? "")
        && !this.nodeMap.has(member.uuid) && !inventory.has(member.uuid));
      statuses.set(appGroupNode.uuid, missing ? "missing" : "silent");
    }

    return statuses;
  }

  generateCells(visualisationData: AppGroupsVisualisationData, directionalLinks: boolean) : CellGenerationResult {
    // let result = [];

    let nodesCells: dia.Cell[] = [];
    let subNodesCells: dia.Cell[] = [];
    let linksCells: dia.Cell[] = [];
    let subLinksCells: dia.Cell[] = [];


    let logicalGraph = visualisationData.graph;
    let appGroundNodes = logicalGraph.nodes.filter((node) => node.type == "appGroup")
    let showAppsGroupsWithoutFlows = this.flowComponent.showAppsGroupsWithoutFlows ?? false;

    let APPS_GROUPS_COLORS = this.getColors();

    // Process nodes
    let graphCells = [];

    let mapDCNetscopeToJointJsUuids: Map<string, {id: string, appGroupId: string}[]> = new Map();
    // Boxes of included apps groups, by id of the cells of the members they hold, down to the first closed
    // one: a box is drawn as soon as one of these is, whether the member got a node of its own or was folded
    // into the box
    let boxCellIdsByMemberCellId: Map<string, string[]> = new Map();

    for (let appGroup of appGroundNodes) {

      let appGroupCell = AppsGroupsRendererJointJs.appGroupToCell(appGroup, this.flowComponent);

      // @ts-ignore
      appGroupCell.attributes.version = 0;

      // this.synchronizer.associateNode(appGroupCell, appGroup);

      let cleanSubNodes = [];
      if (appGroup.subnodes != undefined) {
        cleanSubNodes = appGroup.subnodes.filter((n) => n != undefined);
      }

      // A group none of whose members took part in a flow has no node to draw, unless the groups are drawn
      // whatever their flows: its box, and the boxes of the groups it includes, are then drawn empty
      if (cleanSubNodes.length > 0 || showAppsGroupsWithoutFlows) {
        graphCells.push(appGroupCell);
        nodesCells.push(appGroupCell);

        // The apps groups this one includes are boxes of their own inside of it, and the groups they include
        // boxes inside of theirs, as long as the box holding them is opened
        let boxCells: Map<string, dia.Cell> = new Map();
        for (let box of this.includedBoxesByAppGroupUuid.get(appGroup.uuid) ?? []) {
          let parentCell = box.parent == undefined ? appGroupCell : boxCells.get(box.parent.cellId);
          if (parentCell == undefined || (box.parent != undefined && !box.parent.expanded)) {
            continue;
          }
          let boxCell = AppsGroupsRendererJointJs.includedAppsGroupToCell(box, cleanSubNodes, this.flowComponent);

          parentCell.embed(boxCell);
          graphCells.push(boxCell);
          subNodesCells.push(boxCell);
          // @ts-ignore
          boxCell.attributes.parent = parentCell;
          // @ts-ignore
          boxCell.attributes.version = 0;
          // @ts-ignore
          boxCell.attributes.children = [];

          boxCells.set(box.cellId, boxCell);
        }

        for (let appGroupChild of cleanSubNodes) {
          if (appGroupChild == undefined) {
            console.warn("skipping an undefined node");
            continue;
          }
          let appGroupChildCell = AppsGroupsRendererJointJs.appGroupMemberToCell(appGroupChild, appGroup, this.flowComponent);

          if (!mapDCNetscopeToJointJsUuids.has(appGroupChild.uuid)) {
            mapDCNetscopeToJointJsUuids.set(appGroupChild.uuid, []);
          }
          // mapDCNetscopeToJointJsUuids.set(appGroupChild.uuid, appGroupChildCell.id.toString());
          mapDCNetscopeToJointJsUuids.get(appGroupChild.uuid).push({
            id: appGroupChildCell.id.toString(),
            appGroupId: appGroup.uuid
          });

          let boxes = this.includedBoxesByMember.get(appGroup.uuid)?.get(appGroupChild.uuid) ?? [];
          let closedIndex = boxes.findIndex((box) => !box.expanded);
          let holdingBoxes = closedIndex >= 0 ? boxes.slice(0, closedIndex + 1) : boxes;
          if (holdingBoxes.length > 0) {
            boxCellIdsByMemberCellId.set(appGroupChildCell.id.toString(), holdingBoxes.map((box) => box.cellId));
          }

          // A closed box stands for its members, which get no node at all
          if (closedIndex >= 0) {
            continue;
          }

          let parentCell = boxes.length > 0 ? boxCells.get(boxes[boxes.length - 1].cellId) : appGroupCell;

          parentCell.embed(appGroupChildCell);
          graphCells.push(appGroupChildCell);
          subNodesCells.push(appGroupChildCell);
          // @ts-ignore
          appGroupChildCell.attributes.parent = parentCell;
          // @ts-ignore
          appGroupChildCell.attributes.version = 0;
          // @ts-ignore
          parentCell.attributes.children.push(appGroupChildCell);
        }
      }
    }

    let mapProcessedLinks = new Map<string, boolean>();
    // Ids of the links drawn so far, see the hyperlinks below
    let displayedLinkIds = new Set<string>();

    // Process links
    for (let link of logicalGraph.links) {

      let linkSortedKey = [link.source.uuid, link.target.uuid].sort().join("__");
      let linkKey = `${link.source.uuid}__${link.target.uuid}`;

      // @ts-ignore
      let flowDebugNames = `${link.source.name}__${link.target.name}`;

      let sourceMarker = undefined
      if (!directionalLinks) {
        linkKey = linkSortedKey;
        // @ts-ignore
        const defaultMarker = shapes.standard.Link.prototype.defaults.attrs.line.targetMarker;
        sourceMarker = {...defaultMarker}
      }

      if (mapProcessedLinks.has(linkKey)) {
        continue;
      }
      mapProcessedLinks.set(linkKey, true);

      // Get the default marker from an existing link
      const linkCell = new shapes.standard.Link({
        id: linkKey,
        source: {id: link.source.uuid},
        target: {id: link.target.uuid},
        connector: {name: 'rounded'},
        visibilityLevel: "high",
        internal: link.source.uuid == link.target.uuid,
        sublinks: [],
        attrs: {
          line: {
            stroke: APPS_GROUPS_COLORS.linkColor,
            strokeWidth: 3,
            sourceMarker: sourceMarker,
          },
          model: {
            source: link.source,
            target: link.target,
            flow: link
          },
        }
      });

      // if (this.mode == "limited") {
      //   graphCells.push(linkCell);
      // }
      // linksCells.push(linkCell);

      // @ts-ignore
      linkCell.attributes.namesKey = flowDebugNames

      let subLinks: joint.dia.Link[] = [];

      for (let subflow of link.subflows) {
        // console.time("plop");
        let sourceUuidWithAppsGroups = mapDCNetscopeToJointJsUuids.get(subflow.source.uuid);
        let targetUuidWithAppsGroups = mapDCNetscopeToJointJsUuids.get(subflow.target.uuid);
        // console.timeEnd("plop");

        // A resource is drawn once per apps group it belongs to. When both ends of the flow have a copy under
        // the same apps group, the flow is drawn there only: linking these copies to the ones under other apps
        // groups would show the flow going to a group it never reached.
        let sharesAppsGroup = (sourceUuidWithAppsGroups ?? []).some((source) =>
          (targetUuidWithAppsGroups ?? []).some((target) => target.appGroupId == source.appGroupId));

        for (let sourceObjectWithAppsGroup of sourceUuidWithAppsGroups) {
          for (let targetObjectWithAppsGroup of targetUuidWithAppsGroups) {
            if (sharesAppsGroup && sourceObjectWithAppsGroup.appGroupId != targetObjectWithAppsGroup.appGroupId) {
              continue;
            }
        // for (let i = 0; i < sourceUuidWithAppsGroups.length; i++) {
        //   for (let j = 0; j < targetUuidWithAppsGroups.length; j++) {
        //     const sourceUuidWithAppsGroup = sourceUuidWithAppsGroups[0];
        //     const targetUuidWithAppsGroup = targetUuidWithAppsGroups[0];
            // let sourceUuidWithAppsGroup = mapDCNetscopeToJointJsUuids.get(subflow.source.uuid);
            // let targetUuidWithAppsGroup = mapDCNetscopeToJointJsUuids.get(subflow.target.uuid);

            let sourceUuidWithAppsGroup = sourceObjectWithAppsGroup.id;
            let targetUuidWithAppsGroup = targetObjectWithAppsGroup.id;

            let subFlowLinkSortedKey = [sourceUuidWithAppsGroup, targetUuidWithAppsGroup].sort().join("__");
            let subFlowLinkKey = `${sourceUuidWithAppsGroup}__${targetUuidWithAppsGroup}`;
            let subFlowDebugNames = `${subflow.source.name}__${subflow.target.name}`;

            let sourceMarker = undefined
            if (!directionalLinks) {
              subFlowLinkKey = subFlowLinkSortedKey;
              // @ts-ignore
              const defaultMarker = shapes.standard.Link.prototype.defaults.attrs.line.targetMarker;
              sourceMarker = {...defaultMarker}
            }

            if (mapProcessedLinks.has(subFlowLinkKey)) {
              continue;
            }
            mapProcessedLinks.set(subFlowLinkKey, true);

            const subLinkCell = new shapes.standard.Link({
              id: subFlowLinkKey,
              source: {id: sourceUuidWithAppsGroup},
              target: {id: targetUuidWithAppsGroup},
              parentLink: linkCell,
              // router: routingParameter,
              connector: {name: 'rounded'},
              visibilityLevel: "low",
              attrs: {
                line: {
                  stroke: APPS_GROUPS_COLORS.linkColor,
                  strokeWidth: 3,
                  sourceMarker: sourceMarker,
                },
                model: {
                  source: subflow.source,
                  target: subflow.target,
                  flow: subflow,
                  appsGroupsIds: {
                    source: sourceObjectWithAppsGroup.appGroupId,
                    target: targetObjectWithAppsGroup.appGroupId,
                  }
                },
                // flow: subflow
              }
            });

            // @ts-ignore
            linkCell.attributes.sublinks.push(subLinkCell);

            // @ts-ignore
            subLinkCell.attributes.namesKey = subFlowDebugNames

            subLinks.push(subLinkCell);

            // if (this.mode == "full") {
            //   graphCells.push(subLinkCell);
            // }
            // subLinksCells.push(subLinkCell);

            // if (!this.flowMap.has(subFlowLinkKey)) {
            //   this.flowMap.set(subFlowLinkKey, []);
            // }
            // this.flowMap.get(subFlowLinkKey).push(subflow);
            //
            // if (!this.flowMap.has(subflow.source.uuid)) {
            //   this.flowMap.set(subflow.source.uuid, []);
            // }
            // this.flowMap.get(subflow.source.uuid).push(subflow);
            // if (!this.flowMap.has(subflow.target.uuid)) {
            //   this.flowMap.set(subflow.target.uuid, []);
            // }
            // this.flowMap.get(subflow.target.uuid).push(subflow);

          }
        }
      }
      // Generate hyperlink and linkcells
      let hyperLink = new HyperLink(linkCell, subLinks, AppsGroupsRendererSimplificationLevels.groupClients, visualisationData, this.memberEndpoint, this.showsInternalFlows, this.classification);
      for (let link of hyperLink.getDisplayedLinks()) {
        // The links between two apps groups in both directions fold into the same displayed links, which are
        // only drawn once
        if (displayedLinkIds.has(link.id.toString())) {
          continue;
        }
        displayedLinkIds.add(link.id.toString());

        let linkCell: shapes.standard.Link = link;
        graphCells.push(linkCell);
      }
    }

    this.spreadParallelLinks(graphCells.filter((n) => n.attributes.type == "standard.Link"));

    let linkCellUuidsMap: Map<string, boolean> = new Map();
    let cellsLinks = graphCells
      .filter((n) => n.attributes.type == "standard.Link");
    for(let cellLink of cellsLinks) {
      let linkRealIdParts = cellLink.id.split("__")
      let srcPart = linkRealIdParts[0];
      let dstPart = linkRealIdParts[1];
      linkCellUuidsMap.set(srcPart, true);
      linkCellUuidsMap.set(dstPart, true);

      // linkCellUuidsMap.set(cellLink.attributes.source.id, true);
      if (cellLink.attributes.source.appGroupId != undefined) {
        linkCellUuidsMap.set(cellLink.attributes.source.appGroupId, true);
      }
      // linkCellUuidsMap.set(cellLink.attributes.target.id, true);
      if (cellLink.attributes.target.appGroupId != undefined) {
        linkCellUuidsMap.set(cellLink.attributes.target.appGroupId, true);
      }
    }

    // Read from the links alone, before the members the user asked to see are kept below whatever their flows
    this.flowComponent.appsGroupsFlowsStatus = this.computeFlowsStatus(appGroundNodes, new Set(linkCellUuidsMap.keys()));

    // A box no flow reaches tells so under its name, which takes a line more
    for (let cell of graphCells) {
      let flowsStatus = this.flowComponent.appsGroupsFlowsStatus.get(cell.id.toString());
      if (cell.attributes.data?.type == "included" && flowsStatus != undefined && flowsStatus != "flows") {
        let size = (cell as dia.Element).size();
        (cell as dia.Element).resize(size.width, size.height + 18);
      }
    }

    // Only the nodes an edge points to survive the filtering below. The members the user asked to see are
    // kept whatever their flows, as a member that exchanged nothing is the endpoint of no edge at all.
    for (let [memberUuid, memberCells] of mapDCNetscopeToJointJsUuids) {
      for (let memberCell of memberCells) {
        if (!this.isMemberVisible(memberUuid, memberCell.appGroupId)) {
          continue;
        }
        // A folded apps group stands for all of its members, the ones the user asked to see included
        if (this.isAppGroupFolded(memberCell.appGroupId)) {
          continue;
        }
        linkCellUuidsMap.set(memberCell.id, true);
        linkCellUuidsMap.set(memberCell.appGroupId, true);
      }
    }

    // A box is drawn as soon as one of the members it holds is kept, even a member the user asked to see
    // while the box is closed: the box then stands for it
    for (let [memberCellId, boxCellIds] of boxCellIdsByMemberCellId) {
      if (linkCellUuidsMap.has(memberCellId)) {
        boxCellIds.forEach((boxCellId) => linkCellUuidsMap.set(boxCellId, true));
      }
    }

    // A box is drawn inside the boxes holding it: these are kept as well, even when the flows all end on the
    // boxes inside of them. A box comes after the ones holding it, so going backwards keeps a whole chain.
    for (let boxes of this.includedBoxesByAppGroupUuid.values()) {
      for (let box of [...boxes].reverse()) {
        if (box.parent != undefined && linkCellUuidsMap.has(box.cellId)) {
          linkCellUuidsMap.set(box.parent.cellId, true);
        }
      }
    }

    // Every group is kept, with the boxes of the groups it includes: a box was only made above when the boxes
    // holding it are opened
    if (showAppsGroupsWithoutFlows) {
      for (let appGroup of appGroundNodes) {
        linkCellUuidsMap.set(appGroup.uuid, true);
        for (let box of this.includedBoxesByAppGroupUuid.get(appGroup.uuid) ?? []) {
          linkCellUuidsMap.set(box.cellId, true);
        }
      }
    }

    console.log("Filtering links and nodes that are displayed")
    let membersCells = graphCells.filter((c) => c.id.toString().includes("->") && !c.id.toString().includes("__"));

    graphCells = graphCells
      .filter((n) => n.attributes.type == "standard.Link" || linkCellUuidsMap.has(n.attributes["id"]) || linkCellUuidsMap.has(n.id) );

    nodesCells = nodesCells
      .filter((n) => linkCellUuidsMap.has(n.attributes["id"]));

    subNodesCells = subNodesCells
      .filter((n) => linkCellUuidsMap.has(n.attributes["id"]));

    // A group drawn without any of its members has nothing embedded (see showAppsGroupsWithoutFlows)
    for (let nodeCell of nodesCells) {
      if (nodeCell.attributes["embeds"] != undefined) {
        nodeCell.attributes["embeds"] = nodeCell.attributes["embeds"].filter((uuid) => linkCellUuidsMap.has(uuid));
      }
    }

    for (let nodeCell of graphCells) {
      if (nodeCell.attributes.embeds != undefined) {
        nodeCell.attributes["embeds"] = nodeCell.attributes["embeds"].filter((uuid) => linkCellUuidsMap.has(uuid));
      }
    }

    let result = {
      "allCells": graphCells,
      "nodesCells": nodesCells,
      "subNodesCells": subNodesCells,
      "linksCells": linksCells,
      "subLinksCells": subLinksCells,
    };
    return result;
  }

  /**
   * Cells of the groups that exchanged no flow (see computeFlowsStatus), with everything drawn inside of them, in
   * the order of the cells given: a box before the ones it holds.
   */
  private cellsOfGroupsWithoutFlows(cells: dia.Cell[], groupCells: dia.Cell[]): dia.Cell[] {
    let statuses = this.flowComponent.appsGroupsFlowsStatus ?? new Map();
    let cellsById: Map<string, dia.Cell> = new Map(cells.map((cell) => [cell.id.toString(), cell]));
    let ids: Set<string> = new Set();

    let addWithEmbeds = (cell: dia.Cell) => {
      ids.add(cell.id.toString());
      for (let childId of cell.attributes["embeds"] ?? []) {
        let child = cellsById.get(childId.toString());
        if (child != undefined) {
          addWithEmbeds(child);
        }
      }
    };
    groupCells
      .filter((cell) => (statuses.get(cell.id.toString()) ?? "flows") != "flows")
      .forEach(addWithEmbeds);

    return cells.filter((cell) => ids.has(cell.id.toString()));
  }

  /**
   * Adds the groups that exchanged no flow to the graph, once the others are laid out: in rows below them, each
   * group with what is drawn inside of it packed in rows as well.
   */
  private placeGroupsWithoutFlows(cells: dia.Cell[], distance: number) {
    if (cells.length == 0) {
      return;
    }

    let bbox = this.graph.getElements().length > 0 ? this.graph.getBBox() : undefined;
    this.graph.addCells(cells);

    let gap = 30;
    let headerHeight = 45;
    let childrenOf = (cell: dia.Cell): dia.Element[] => (cell.attributes["embeds"] ?? [])
      .map((id) => this.graph.getCell(id))
      .filter((child) => child != undefined && child.isElement()) as dia.Element[];

    // Lays a cell out with what it holds, from (0, 0): the cells inside of it in rows, and itself fitted to them
    let pack = (cell: dia.Element) => {
      let children = childrenOf(cell);
      if (children.length == 0) {
        cell.position(0, 0);
        return;
      }
      let x = 10;
      let y = headerHeight;
      let rowHeight = 0;
      for (let child of children) {
        pack(child);
        let size = child.size();
        if (x > 10 && x + size.width > 900) {
          x = 10;
          y += rowHeight + gap;
          rowHeight = 0;
        }
        child.position(x, y, {deep: true});
        x += size.width + gap;
        rowHeight = Math.max(rowHeight, size.height);
      }
      this.fitParentNodeWithChildren(cell, this.graph);
    };

    let left = bbox?.x ?? distance;
    let rowsWidth = Math.max(bbox?.width ?? 0, 1600);
    let x = left;
    let y = bbox != undefined ? bbox.y + bbox.height + 2 * distance : distance;
    let rowHeight = 0;
    let topCells = cells.filter((cell) => cell.isElement() && !cells.some((other) =>
      (other.attributes["embeds"] ?? []).includes(cell.id))) as dia.Element[];
    for (let cell of topCells) {
      pack(cell);
      let size = cell.size();
      if (x > left && x + size.width > left + rowsWidth) {
        x = left;
        y += rowHeight + distance / 2;
        rowHeight = 0;
      }
      cell.position(x, y, {deep: true});
      x += size.width + distance / 2;
      rowHeight = Math.max(rowHeight, size.height);
    }
  }

  static appGroupToCell(appGroup: FlowRessourceObject, flowComponent: FlowViewHost) {
    let appGroupObject: AppsGroups = undefined;
    for (let appGroupObjectCandidate of flowComponent.groupsApps) {
      if (appGroupObjectCandidate.name == appGroup.name) {
        appGroupObject = appGroupObjectCandidate
      }
    }

    let appGroupCell = new AngularJointJsComponent({
      // position: {
      //   x: 300,
      //   y: 220
      // },
      data: {
        appGroup: appGroup,
        appGroupObject: appGroupObject,
        type: "parent",
        flowComponent: flowComponent
      },
    });
    appGroupCell.id = appGroup.uuid;
    // @ts-ignore
    appGroupCell.attributes.id = appGroup.uuid;
    // @ts-ignore
    appGroupCell.attributes.children = [];
    return appGroupCell;
  }

  /**
   * Cell of an apps group included in another one. It is sized as a member while it is closed, and fitted to
   * the members it holds once opened, as the including group is.
   */
  static includedAppsGroupToCell(box: IncludedAppsGroupBox, appGroupMembers: FlowRessourceObject[], flowComponent: FlowViewHost) {
    let includedAppGroup = new FlowRessourceObject();
    includedAppGroup.uuid = box.cellId;
    includedAppGroup.name = box.name;
    includedAppGroup.type = "includedAppGroup";
    includedAppGroup.address = "0.0.0.0";
    // The side panel lists the flows of these, when the box is double clicked
    includedAppGroup.subnodes = appGroupMembers.filter((member) => box.memberUuids.has(member.uuid));
    includedAppGroup.inclusion = box.toState();

    let boxCell = new AngularJointJsComponent({
      size: {
        width: Math.max(180, box.name.length * 8 + 90),
        height: 70
      },
      data: {
        appGroup: includedAppGroup,
        type: "included",
        flowComponent: flowComponent
      },
    });

    boxCell.id = box.cellId;
    // @ts-ignore
    boxCell.attributes.id = box.cellId;

    return boxCell;
  }

  static appGroupMemberToCell(member: FlowRessourceObject, appGroup: FlowRessourceObject, flowComponent: FlowViewHost) {
    let appGroupMemberCell = new AngularJointJsComponent({
      // position: {
      //   x: 300,
      //   y: 220
      // },
      size: {
        width: 160,
        height: 70
      },
      data: {
        appGroup: member,
        type: "member",
        flowComponent: flowComponent
      },
    });

    appGroupMemberCell.id = `${appGroup.uuid}->${member.uuid}`;
    // @ts-ignore
    appGroupMemberCell.attributes.id = appGroupMemberCell.id;
    // @ts-ignore
    appGroupMemberCell.attributes.parent = undefined;

    return appGroupMemberCell;
  }

  getColors() {
    return {
      appGroupFill: 'rgba(195,201,213,0.8)',
      appGroupStroke: '#5b6dae',
      appGroupTextColor: this.flowComponent.isDarkMode ? '#cccfe1' : '#24335a',

      vmFill: 'rgba(80,216,248,0.53)',
      vmStroke: '#5b6dae',
      vmTextColor: '#24335a',

      linkColor: this.flowComponent.isDarkMode ? 'rgb(227,224,224)' : 'rgb(0, 0, 0)'
    };
  }

  override async render(flowComponent: FlowViewHost): Promise<void> {
    let renderingNumber = ++this.renderingNumber;
    let thisRenderer = this;

    let isNewRendering = this.graphData == undefined;

    console.log("[apps-groups] rendering (1)");

    this.flowComponent = flowComponent;

    let flowsRepository = this.flowComponent.flowsRepository;
    let dataKey = this.loadedDataKey(flowComponent);
    let loadedData = this.loadedData;
    let dataLoaded = loadedData != undefined && loadedData.flowsRepository === flowsRepository
      && loadedData.key == dataKey;

    let graphData = dataLoaded ? loadedData.graphData : await flowsRepository.generateGraphData(true);
    if (renderingNumber != this.renderingNumber) {
      return;
    }


    console.log("[apps-groups] rendering (2)");


    this.graphData = graphData;

    // The links are split by flows descriptions when the user asked for it
    if (dataLoaded) {
      this.classification = loadedData.classification;
    } else {
      try {
        this.classification = await FlowsDescriptionsClassification.classify(
          flowComponent.appsGroupsLinksMode, flowsRepository, flowComponent.netscopeService);
      } catch (e) {
        console.warn("[apps-groups] the flows could not be matched against the flows descriptions, the links ignore them", e);
        this.classification = undefined;
      }
      if (renderingNumber != this.renderingNumber) {
        return;
      }
      this.loadedData = {key: dataKey, flowsRepository: flowsRepository, graphData: graphData,
                         classification: this.classification};
    }

    let directionalLinks = false;

    this.flowComponent = flowComponent;
    let appsGroups = this.flowComponent.groupsApps;

    // TODO: Routers are a messy apps groups, I will remove them in a near future.
    appsGroups = appsGroups
      .filter((ag) => ag.name != "routers");

    console.log("[apps-groups] rendering (3)");

    let visualizationData = this.computeVisualizationData(graphData, appsGroups, [], false);
    this.setDatatableFlows();
    // visualizationData = this.generateBigFakeGraphData();

    console.log("[apps-groups] rendering (4)");

    let cellGenerationResult = this.generateCells(visualizationData, directionalLinks);
    let graphCells = cellGenerationResult.allCells;

    // TODO: remove this for debugging
    // graphCells = [];

    let linksCellCount = cellGenerationResult.allCells.length - cellGenerationResult.nodesCells.length;
    console.log(`cellGenerationResult.allCells.length = ${cellGenerationResult.allCells.length}`);

    console.log("[apps-groups] rendering (4.5)");


    console.log(`linksCellCount: ${linksCellCount}`);

    if (flowComponent.routingAlgorithm == "automatic") {
      if (linksCellCount > 5000) {
        this.mode = "limited";
      } else {
        if (linksCellCount > 50) {
        // if (cellGenerationResult.subLinksCells.length > 1) {
          this.routerAlgorithm = "flat";
          this.routerAlgorithm = "optimized";
          this.routerAlgorithm = "hybrid";
          // this.routerAlgorithm = "hybrid";
          this.flowComponent.isSlow = true;
        } else {
          this.routerAlgorithm = "libavoid";
          this.flowComponent.isSlow = false;
        }
      }

      if (this.mode == "limited") {
        // this.routerAlgorithm = "optimized";
        this.routerAlgorithm = "hybrid";
      }
    } else {
      this.routerAlgorithm = flowComponent.routingAlgorithm;
    }
    flowComponent.currentRoutingAlgorithm = this.routerAlgorithm;

    // this.routerAlgorithm = "optimized";
    // this.routerAlgorithm = "libavoid";
    // this.routerAlgorithm = "flat";
    // this.routerAlgorithm = "hybrid";

    ////////////////////////////////
    // Create JointJS graph
    ////////////////////////////////
    console.log("[apps-groups] rendering (5)");
    this.graph = new dia.Graph({}, {cellNamespace: shapes});

    this.flowComponent.rendererVersion = 0;
    // @ts-ignore
    this.graph.version = this.flowComponent.rendererVersion;

    let membersCells = graphCells.filter((c) => c.id.toString().includes("->") && !c.id.toString().includes("__"));

    // The groups that exchanged no flow are laid out apart, below the others (see placeGroupsWithoutFlows): the
    // layout would otherwise scatter them among the groups the links join
    let cellsWithoutFlows = this.cellsOfGroupsWithoutFlows(graphCells, cellGenerationResult.nodesCells);
    this.graph.addCells(graphCells.filter((cell) => !cellsWithoutFlows.includes(cell)));

    // Create a subgraph. The cells of the groups without flows are left out: a cell belongs to the first graph it
    // is added to, theirs would be these ones rather than the main one, and they would neither find what they
    // hold nor move it along
    const subNodesGraph = new dia.Graph({}, {cellNamespace: shapes});
    let subNodesGraphCells = cellGenerationResult.subNodesCells.concat(cellGenerationResult.subLinksCells)
      .filter((cell) => !cellsWithoutFlows.includes(cell));
    subNodesGraph.addCells(subNodesGraphCells);

    const nodesGraph = new dia.Graph({}, {cellNamespace: shapes});
    let nodesGraphCells = cellGenerationResult.nodesCells.concat(cellGenerationResult.linksCells)
      .filter((cell) => !cellsWithoutFlows.includes(cell));
    nodesGraph.addCells(nodesGraphCells);

    ////////////////////////////////
    // Adjust nodes sizes
    ////////////////////////////////
    let additionalSizeForEachLink = 10.0;

    console.log("[apps-groups] rendering (6)");


    let nodes = this.graph.getCells();

    for (let cell of this.graph.getCells()) {
      if (!cell.attributes["embeds"]) {
        continue;
      }

      for (let childCellId of cell.attributes["embeds"]) {
        let childCell = this.graph.getCell(childCellId);
        let childCellLinkCount = this.graph.getConnectedLinks(childCell).length;

        // @ts-ignore
        let existingSizes = childCell.resize(
          // @ts-ignore
          childCell.attributes.size.width + childCellLinkCount * additionalSizeForEachLink,
          // @ts-ignore
          childCell.attributes.size.height + childCellLinkCount * additionalSizeForEachLink
        );
      }
    }

    ////////////////////////////////
    // DRAW GRAPH
    ////////////////////////////////
    console.log("[apps-groups] rendering (7)");
    // Create the custom view class with Angular DI
    const AngularElementView = createAngularElementView(
      flowComponent.appRef,
      flowComponent.injector
    );

    var paper = new dia.Paper({
      el: document.getElementById('divFlowsSvg'),
      model: this.graph,
      width: '100%',
      height: '100%',
      gridSize: 10,
      // background: { color: '#f8fafc' },
      clickThreshold: 5,
      cellViewNamespace: {
        ...shapes,
        // Use the custom Angular element view for 'AngularElement' type
        AngularElementView,
      },
      interactive: {
        elementMove: true,
        linkMove: false,
      },
      linkPinning: false,
      multiLinks: false,
      // Allow default browser behavior (e.g. blur inputs) when clicking
      // on the paper's blank area or on element/link views
      preventDefaultBlankAction: false,
      preventDefaultViewAction: false,
    });
    this.paper = paper;

    // TODO: fix bug below, when connecting apps groups together, instead of vms
    // let layout = DirectedGraph.layout(this.graph, {
    //   setLinkVertices: false,
    //   clusterPadding: {top: 10, left: 10, right: 10, bottom: 10}
    // });

    console.log("[apps-groups] rendering (8)");


    let distance = 110;
    let padding = 60;

    if (this.routerAlgorithm == "optimized" || this.routerAlgorithm == "flat" || this.routerAlgorithm == "hybrid") {
      // distance = 60;
      // padding = 20;
    }

    // Freeze the graph to suppress individual change events
    this.graph.startBatch('layout');

    console.log(EdgeRoutingMode);

    const takeRandom = <T>(arr: T[], n: number): T[] => {
      const result = [...arr];
      for (let i = 0; i < n; i++) {
        const j = i + Math.floor(Math.random() * (result.length - i));
        [result[i], result[j]] = [result[j], result[i]];
      }
      return result.slice(0, Math.min(n, arr.length));
    };

    const links = this.graph.getLinks();

    console.log("Sampling maximum 200 links");
    const sampledLinks = takeRandom(links, 200);

    let sampledLinksIndex: Map<string | number, string | number> = new Map<string | number, string | number>();
    for (let link of sampledLinks) {
      sampledLinksIndex.set(link.id, link.id)
    }

    links
      .filter(link => ! sampledLinksIndex.has(link.id))
      .forEach(link => link.remove());

    let optimizer = new GraphOptimizer();
    this.graph = optimizer.optimizeGraph(this.graph);

    try {
      const { bbox } = layout(this.graph, {
        layerDirection: LayerDirectionEnum.TB,
        edgeRoutingMode: EdgeRoutingMode.Rectilinear,
        layerSeparation: distance,
        nodeSeparation: distance,
        clusterPadding: {
          top: padding,
          bottom: padding,
          left: padding,
          right: padding,
        },
        x: distance,
        y: distance,
        getSize: (element) => {
          const size = element.size();
          return {
            width: size.width + padding,
            height: size.height + padding,
          };
        },

        setAnchor: (link, sourceAnchor, targetAnchor) => {
          // override anchor positions after layout if needed
          link.source(link.getSourceElement(), {
            anchor: {
              name: 'modelCenter',
              args: sourceAnchor,
            },
            connectionPoint: { name: 'boundary' } // or 'bbox'
          });
          link.target(link.getTargetElement(), {
            anchor: {
              name: 'modelCenter',
              args: targetAnchor
            },
            connectionPoint: { name: 'boundary' } // or 'bbox'
          });
        }
      });
    } catch (e) {
      let linksError = this.graph.getLinks();
      console.log(linksError);
      console.error(e);
    }


    this.placeGroupsWithoutFlows(cellsWithoutFlows, distance);

    let allLinks2 = this.graph.getLinks();
    console.log(allLinks2);

    // this.graph.getLinks().forEach(link => {
    //   link.set('router', { name: 'orthogonal' }); // or 'manhattan'
    // });

    // Set padding to zero, now that MSAGL has computed the nodes positions
    // padding = 0;

    // Step 5: Create the avoid router and let it take over link routing
    await AvoidRouter.setup();
    if (renderingNumber != this.renderingNumber) {
      return;
    }

    if (this.routerAlgorithm == "libavoid") {
      this.avoidRouter = new AvoidRouter(this.graph, {
        shapeBufferDistance: 15,
        idealNudgingDistance: 5,
      });
      this.avoidRouter.ignoreVerticesSet = false;
      this.avoidRouter.onRoutingChange = (routing) => {
        flowComponent.linksRouting = routing;
        if (!routing) {
          paper.el.classList.remove("links-awaiting-routes");
        }
      };
      this.graph.getLinks().forEach(link => {
        link.set('router', null);
        // @ts-ignore
        link.isDirty = true;
      });
      this.graph.getElements().forEach((el) => {
        // @ts-ignore
        el.isDirty = true;
      });
      // The links are routed once they are all back in the graph (see below): routing the sampled ones alone
      // here would be thrown away
    } else {
      // this.avoidRouter = new optimizedRouter(this.graph, {
      //   shapeBufferDistance: 15,
      //   idealNudgingDistance: 10,
      //   paper: paper
      // });
      if (this.routerAlgorithm == "optimized" || this.routerAlgorithm == "hybrid") {
        this.avoidRouter = new OptimizedAppsGroupsRouter(this.graph, {
          shapeBufferDistance: 15,
          idealNudgingDistance: 10,
          paper: paper
        });
      } else {
        this.avoidRouter = new FlatAppsGroupsRouter(this.graph, {
          shapeBufferDistance: 15,
          idealNudgingDistance: 10,
          paper: paper
        });
      }
      // @ts-ignore
      joint.routers.bundled = parallelRoute(this.avoidRouter.routingFunction);
      this.graph.getLinks().forEach(link => {
        if (this.routerAlgorithm == "optimized" || this.routerAlgorithm == "hybrid") {
          link.set('router', {
            name: 'bundled',
            args: {
              maximumLoops: 1000,
              routerAlgorithm: this.routerAlgorithm
            }
            ,
            source: {
              anchor: {
                name: 'modelCenter',
              },
            },
            target: {
              anchor: {
                name: 'modelCenter',
              },
            },
          });
        }

        link.set('vertices', []);
      });
    }

    console.log("Restoring links that were not in the sampling");
    links
      .filter(link => ! sampledLinksIndex.has(link.id))
      .forEach(link => this.graph.addCell(link));

    // Single re-render after all positions are applied
    this.graph.stopBatch('layout');

    // ElkPlacement.adjustWithElk(this.graph, "layered", true);

    console.log("[apps-groups] rendering (9)");

    this.graph.on('change:position', (cell) => {
      flowComponent.rendererVersion += 1;
      // @ts-ignore
      this.graph.version = flowComponent.rendererVersion;
      // return;

      // update cell version
      if (cell.attributes.version == undefined) {
       cell.attributes.version = 0;
      }
      cell.attributes.version += 1;

      this.avoidRouter.clean(cell);

      let connectedLinks = thisGraph.getConnectedLinks(cell);
      // let linksCells = graphCells.filter((cell) => cell.attributes["type"] == "standard.Link");

      setTimeout(() => {
        connectedLinks.map((link) => {
          // @ts-ignore
          link.isDirty = true;
          // @ts-ignore
          // link.findView(paper).requestConnectionUpdate();
          if (this.routerAlgorithm == "optimized" || this.routerAlgorithm == "flat" || this.routerAlgorithm == "hybrid") {
            link.unset("vertices");
          }
        });
      }, 500);
      // Update parent
      // @ts-ignore
      cell.isDirty = true;
      if (cell.attributes.parentCell) {
        thisRenderer.fitParentNodeWithChildren(cell.attributes.parentCell, thisRenderer.graph);
        // @ts-ignore
        cell.attributes.parentCell.isDirty = true;
      } else {
        // Mark all children as dirty
        let childrenCells = cell.getEmbeddedCells();
        childrenCells.map((childCell) => {
          // @ts-ignore
          childCell.isDirty = true;
        })
      }
      thisRenderer.linksShouldBeUpdated = true;
    });

    /////////////////////////
    // Add mouse interactions
    /////////////////////////

    console.log("[apps-groups] rendering (10)");


    // Panning
    let isPanning = false;
    let startX, startY, originX, originY;

    paper.on('blank:pointerdown', (evt, x, y) => {
      isPanning = true;
      startX = evt.clientX;
      startY = evt.clientY;
      const translate = paper.translate();
      originX = translate.tx;
      originY = translate.ty;
    });

    document.addEventListener('mousemove', (evt) => {
      if (isPanning) {
        paper.translate(
          originX + (evt.clientX - startX),
          originY + (evt.clientY - startY)
        );
      }
    });

    document.addEventListener('mouseup', () => {
      isPanning = false;
    });

    // Zooming
    const MIN_ZOOM = 0.1;
    const MAX_ZOOM = 10;

    function getViewport(paper: dia.Paper) {
      const scale = paper.scale();
      const translate = paper.translate();
      const paperEl = paper.el;

      const viewportWidth = paperEl.clientWidth;
      const viewportHeight = paperEl.clientHeight;

      return {
        x: -translate.tx / scale.sx,
        y: -translate.ty / scale.sy,
        width: viewportWidth / scale.sx,
        height: viewportHeight / scale.sy
      }
    }

    let thisGraph = this.graph;

    paper.freeze();
    this.graph.startBatch('set-optimized-router');

    this.graph.getLinks().forEach((link) => {

      // TODO: optimise here

      if (this.routerAlgorithm == "optimized" || this.routerAlgorithm == "hybrid") {
        // link.set('router', null);
        link.set('router', {
          name: 'bundled',
          args: {
            maximumLoops: 1000,
            routerAlgorithm: this.routerAlgorithm
          }
        });
        // link.set('vertices', []);
      }
    });
    if (this.routerAlgorithm == "libavoid") {

      // paper.freeze();
      // this.graph.getLinks().forEach((link) => {
      //   // link.set('vertices', []);
      //   // @ts-ignore
      //   link.isDirty = true;
      // });
      // paper.unfreeze();

      // The groups are fitted to their members first, as the interval below would do, so that the links are
      // routed around the boxes as they are drawn and need no routing again
      this.graph.getCells().forEach((cell) => thisRenderer.fitParentNodeWithChildren(cell, this.graph));
      // The boxes are shown first, the links once they are routed: drawn straight meanwhile, they would cross
      // the whole map
      paper.el.classList.add("links-awaiting-routes");
      this.avoidRouter.routeAllSoon();
    }
    this.graph.stopBatch('set-optimized-router');
    paper.unfreeze();

    let linksWithoutRouter = this.graph
        .getLinks()
        .filter((l) => l.get("router") == undefined);

    if (this.routerAlgorithm == "optimized" || this.routerAlgorithm == "hybrid") {
      linksWithoutRouter.forEach((l) => {
        l.set('router', {
          name: 'bundled',
          args: {
            maximumLoops: 1000,
            routerAlgorithm: this.routerAlgorithm
          }
        });
        l.set('vertices', []);
      });
    }

    paper.on('element:mouseenter', (elementView) => {
      const element = elementView.model;

      // Highlight the hovered node
      elementView.highlight();

      // Get connected links and their other-end nodes
      const connectedLinks = thisGraph.getConnectedLinks(element);
      connectedLinks.forEach(link => {
        // Highlight the link
        link.findView(paper).highlight();

        // Highlight the neighbor node
        const neighborId = link.getSourceElement().id === element.id
          ? link.getTargetElement().id
          : link.getSourceElement().id;

        const neighbor = thisGraph.getCell(neighborId);
        if (neighbor) neighbor.findView(paper).highlight();
      });
    });

    paper.on('element:mouseleave', (elementView) => {
      const element = elementView.model;

      // Unhighlight everything
      elementView.unhighlight();

      thisGraph.getConnectedLinks(element).forEach(link => {
        link.findView(paper).unhighlight();

        const neighborId = link.getSourceElement().id === element.id
          ? link.getTargetElement().id
          : link.getSourceElement().id;

        const neighbor = thisGraph.getCell(neighborId);
        if (neighbor) neighbor.findView(paper).unhighlight();
      });
    });

    paper.on('link:mouseenter', (linkView) => {
      const link = linkView.model;

      // Highlight the link itself
      linkView.highlight();

      // Highlight source and target nodes
      const source = link.getSourceElement();
      const target = link.getTargetElement();

      if (source) source.findView(paper).highlight();
      if (target) target.findView(paper).highlight();
    });

    paper.on('link:mouseleave', (linkView) => {
      const link = linkView.model;

      linkView.unhighlight();

      const source = link.getSourceElement();
      const target = link.getTargetElement();

      if (source) source.findView(paper).unhighlight();
      if (target) target.findView(paper).unhighlight();
    });

    paper.el.addEventListener('wheel', (evt) => {
      evt.preventDefault();

      // <new behaviour>
      // @ts-ignore
      let zoomRatio = evt.wheelDelta / 1200;  // I want to have a 0.1 value for a wheelDelta of 120
      const delta = evt.deltaY > 0 ? (0.5 * zoomRatio) : 0.5 * zoomRatio;

      const oldScale = paper.scale().sx;
      const newScale = oldScale + delta;

      if (newScale >= MIN_ZOOM && newScale <= MAX_ZOOM) {
        let paperSize = paper.getComputedSize();

        let coordsBeforeScaling = paper.clientToLocalPoint({ x: evt.pageX, y: evt.pageY });

        paper.scale(newScale, newScale);

        let coordsAfterScaling = paper.clientToLocalPoint({ x: evt.pageX, y: evt.pageY });

        let viewPortAfter = getViewport(paper);

        let driftX = coordsAfterScaling.x - coordsBeforeScaling.x;
        let driftY = coordsAfterScaling.y - coordsBeforeScaling.y;

        const currentTranslate = paper.translate();
        const tx = currentTranslate.tx + driftX * (paperSize.width / viewPortAfter.width);
        const ty = currentTranslate.ty + driftY * (paperSize.height / viewPortAfter.height);

        paper.translate(tx, ty);
      }

      updateVisibility()
      // </new behaviour>

    });

    const ZOOM_THRESHOLD = 0.0; // tune this to your needs

    let updateVisibility = () => {
      let zoom = paper.scale().sx;

      thisGraph.getLinks().forEach(link => {
        const level = link.get('visibilityLevel');
        const view = paper.findViewByModel(link);
        if (!view) return;

        // if (level === 'high' || this.mode == "limited") {
        //   let showLink = (zoom < ZOOM_THRESHOLD)
        //   if (this.mode == "limited") {
        //     // @ts-ignore
        //     showLink = !link.attributes.internal;
        //   }
        //   // High-level links: visible when zoomed OUT, hidden when zoomed IN
        //   view.el.style.display = showLink ? '' : 'none';
        // } else if (level === 'low') {
        //   // Detail links: hidden when zoomed OUT, visible when zoomed IN
        //   view.el.style.display = zoom >= ZOOM_THRESHOLD ? '' : 'none';
        // }
      });
    }

    //////////////////////////////
    // Handle double clicks
    //////////////////////////////

    // Single click + drag = move node (default behavior)
    // Double click = select / open dialog
    paper.on('element:pointerdblclick', (elementView) => {
      console.log('Double clicked', elementView.model.id);
      // @ts-ignore
      let elementId: string = elementView.model.id.toString();
      // let filteredFlows = [];

      let element: FlowRessourceObject = undefined;

      let label = "Unknown value";

      if (this.nodeMap.has(elementId)) {
        let elementObject = this.nodeMap.get(elementId);
        label = elementObject.name;
        element = elementObject;
      } else {
        try {
          // @ts-ignore
          label = elementView.model.attributes.data.appGroupObject?.name ?? elementView.model.attributes.data.appGroup.name;
        } catch(e) {
          console.log("Could not extract a name");
        }
      }

      let whereClause = "where false";

      if (elementId.includes("->")) {
        let elementIdParts = elementId.split("->");
        elementId = elementIdParts[1];
      }

      if (elementId.startsWith("vim.HostSystem") || elementId.startsWith("vim.VirtualMachine") || elementId.startsWith("xo.VirtualMachine") || elementId.startsWith("xo.HostSystem") || elementId.startsWith("nu.VirtualMachine") || elementId.startsWith("nu.HostSystem")) {
        whereClause = `where (src_resource_uuid = '${elementId}' or dst_resource_uuid = '${elementId}') `;
      }
      // @ts-ignore
      else if (elementId.startsWith("vim.ExternalIp") && element != undefined) {
        whereClause = `where (src_ipaddr = '${element.address}' or dst_ipaddr = '${element.address}') `;
      } else {
        // Process a group
        // let childrenIds: string[] = [];

        // @ts-ignore
        let childrenIds = elementView.model.attributes.data.appGroup.subnodes
          .filter((n) => n != undefined)
          .map((n) => n.uuid);

        // // @ts-ignore
        // if (elementView.model.attributes.embeds != undefined) {
        //   // @ts-ignore
        //   childrenIds = elementView.model.attributes.embeds
        // }

        let whereClauses = ["false"]

        // @ts-ignore
        for (let childId of childrenIds) {
          let childWhereClause = ` (src_resource_uuid = '${childId}' or dst_resource_uuid = '${childId}') `;
          whereClauses.push(childWhereClause);
        }
        whereClause = "where " + whereClauses.join(" or ")
      }

      this.flowComponent.askShowSidePanel(label, whereClause, elementId, element);
    });

    // A click on the box of an apps group selects it, a click on the map around the boxes clears the selection.
    // Both only fire when the pointer did not move, a drag or a pan selecting nothing.
    paper.on('element:pointerclick', (elementView) => {
      // @ts-ignore
      let data = elementView.model.attributes.data;
      if (data?.type == "parent") {
        this.flowComponent.selectAppsGroup?.(data.appGroupObject);
      } else if (data?.type == "included" && data.appGroup?.inclusion != undefined) {
        // The box of an included apps group is selected inside the apps group drawing it
        let inclusion: IncludedAppsGroupBoxState = data.appGroup.inclusion;
        let appGroup = this.flowComponent.groupsApps
          .find((candidate) => candidate.name == inclusion.includingAppsGroupName);
        this.flowComponent.selectAppsGroup?.(appGroup, inclusion.key);
      }
    });

    paper.on('blank:pointerclick', () => {
      this.flowComponent.selectAppsGroup?.(undefined);
    });

    paper.on('link:pointerclick', (linkView: dia.LinkView) => {
      // @ts-ignore
      let elementId: string = linkView.model.id;
      let links = this.graph.getLinks();
      let correspondingLinks = links.filter((l) => l.id == linkView.model.id);
      console.log(correspondingLinks);

      let linksWithoutRouter = this.graph
        .getLinks()
        .filter((l) => l.get("router") == undefined);

      if (this.routerAlgorithm == "optimized" || this.routerAlgorithm == "hybrid") {
        linksWithoutRouter.forEach((l) => {
          l.set('router', {
            name: 'bundled',
            args: {
              maximumLoops: 1000,
              routerAlgorithm: this.routerAlgorithm
            }
          });
          l.set('vertices', []);
        });
        correspondingLinks.forEach((l) => {
          l.set('router', {
            name: 'bundled',
            args: {
              maximumLoops: 1000,
              routerAlgorithm: this.routerAlgorithm
            }
          });
          l.set('vertices', []);
        });
      }
    });

    paper.on('link:pointerdblclick', (linkView: dia.LinkView) => {
      // @ts-ignore
      let elementId: string = linkView.model.id;
      // @ts-ignore
      let filteredFlows = this.flowComponent.renderer.flowMap.get(elementId);

      if (elementId.includes("->")) {
        let elementIdParts = elementId.split("->");
        elementId = elementIdParts[1];
      }

      // @ts-ignore
      if (linkView.model.attributes.sublinks == undefined) {
        // @ts-ignore
        let sourceObject = filteredFlows[0].source;

        // @ts-ignore
        let targetObject = filteredFlows[0].target;

        let whereClause = `where (src_resource_uuid = '${sourceObject.uuid}' and dst_resource_uuid = '${targetObject.uuid}') or (dst_resource_uuid = '${sourceObject.uuid}' and src_resource_uuid = '${targetObject.uuid}')`;

        let label = `${sourceObject.name} and ${targetObject.name}`;

        this.flowComponent.askShowSidePanel(label, whereClause, undefined, undefined);

      } else {
        let srcElement = linkView.model.getSourceElement();
        let dstElement = linkView.model.getTargetElement();

        /**
         * Resources an end of the link stands for. A member only stands for itself, while the node of an apps
         * group only stands for the members it folds: the ones displayed as nodes of their own carry their
         * flows on their own links, and listing them here would show those flows a second time.
         */
        let endpointUuids = (element): string[] => {
          // @ts-ignore
          if (element.attributes.data.type == "member") {
            // @ts-ignore
            return [element.attributes.data.appGroup.uuid];
          }

          // A closed box stands for every member of its included apps group, an opened one for the folded ones
          // @ts-ignore
          if (element.attributes.data.type == "included") {
            // @ts-ignore
            let inclusion = element.attributes.data.appGroup.inclusion;
            let includingAppGroupUuid = Array.from(this.appGroupNamesByUuid)
              .find(([, name]) => name == inclusion.includingAppsGroupName)?.[0];
            let foldedUuids = inclusion.memberUuids
              .filter((uuid) => !this.isMemberVisible(uuid, includingAppGroupUuid));

            return inclusion.expanded && foldedUuids.length > 0 ? foldedUuids : inclusion.memberUuids;
          }

          // @ts-ignore
          let membersUuids = element.attributes.children.map((c) => c.attributes.data.appGroup.uuid);
          // A folded group stands for all of its members
          if (this.isAppGroupFolded(element.id.toString())) {
            return membersUuids;
          }
          let foldedMembersUuids = membersUuids.filter((uuid) => !this.isMemberVisible(uuid, element.id.toString()));

          // Every member is displayed on its own: the group stands for all of them rather than for nothing
          return foldedMembersUuids.length > 0 ? foldedMembersUuids : membersUuids;
        };

        let srcUuids = endpointUuids(srcElement);
        let dstUuids = endpointUuids(dstElement);

        let srcUuidsStr = JSON.stringify(srcUuids).replaceAll('"', "'");
        let dstUuidsStr = JSON.stringify(dstUuids).replaceAll('"', "'");
        let whereClause = `where (src_resource_uuid IN ${srcUuidsStr} and dst_resource_uuid IN ${dstUuidsStr}) or (src_resource_uuid IN ${dstUuidsStr} and dst_resource_uuid IN ${srcUuidsStr}) `;

        // @ts-ignore
        let sourceObject = srcElement.attributes.data.appGroup.name;

        // @ts-ignore
        let targetObject = dstElement.attributes.data.appGroup.name;

        let label = `${sourceObject} and ${targetObject}`;

        // A link split by the flows descriptions only carries the flows of its category
        let category: LinkCategory = linkView.model.get("category");
        if (category != undefined) {
          whereClause = `where ((${whereClause.replace(/^where /, "")}) and (${category.sqlClause})) `;
          label = `${label} (${category.label ?? category.key})`;
        }

        this.flowComponent.askShowSidePanel(label, whereClause, undefined, undefined);
      }

    });

    paper.on('blank:contextmenu', (e) => {
      console.log("click on map");
    });

    // this.bundleRouter = new LinkBundleRouter(subNodesGraph, {
    //   spread: 12,
    //   edgeRefs: this.avoidRouter.edgeRefs,
    //   getParentSetId: (element) => element.attributes["parent"] ?? null,
    // });

    // this.avoidRouter.startListening();
    this.updateInterval = setInterval(() => {
      if (thisRenderer !== this.flowComponent.renderer) {
        console.log("I detected a new renderer");
        clearInterval(this.updateInterval);
        this.destroy(this.flowComponent);
      } else {
        if (this.linksShouldBeUpdated) {
          this.graph.getCells().map((c) => {
            thisRenderer.fitParentNodeWithChildren(c, this.graph);
          })
          this.linksShouldBeUpdated = false;

          if (this.routerAlgorithm == "libavoid") {
            this.avoidRouter.routeAllSoon();
          }
          if (this.routerAlgorithm == "optimized" || this.routerAlgorithm == "flat" || this.routerAlgorithm == "hybrid") {
          }
        }
      }
    }, 200);

    console.log("[apps-groups] rendering (11)");


    // Show legend
    this.flowComponent.updateLegend(undefined);

    // Update list of resources
    this.allResourcesArray = Array.from(this.nodeMap.values())
      .filter((resource) => resource.type == "vm");


    let allLinks = this.graph.getLinks();
    console.log(`allLinks.length => ${allLinks.length}`);
    // The precise routing already set the routes of the links, the other routers compute them as they are drawn
    if (this.routerAlgorithm != "libavoid") {
      allLinks.forEach((link) => {
        link.unset("vertices");
      });
    }

    console.log("[apps-groups] rendering (12)");

    // Center the graph so that it fits the current view port
    this.fitGraphToView();
    updateVisibility();

    if (this.routerAlgorithm == "libavoid") {
      // Routed above: the interval only routes the links again once a node is moved
      this.linksShouldBeUpdated = false;
    } else if (! isNewRendering) {
      this.linksShouldBeUpdated = true;
    }

    // this.graph.getLinks().map((l) => {
    //   let router = l.get("router");
    //   let vertices = l.get("vertices");
    //   console.log("plouf");
    //   // l.set('router', {
    //   //   name: 'bundled',
    //   //   args: {
    //   //     maximumLoops: 1000,
    //   //     routerAlgorithm: this.routerAlgorithm
    //   //   }
    //   // });
    //   // l.set('vertices', []);
    // });

  }

  override destroy(flowComponent: FlowViewHost) {
    super.destroy(flowComponent);
    this.renderingNumber++;
    if (this.updateInterval) {
      clearInterval(this.updateInterval);
    }
    // The paper exists as soon as the rendering got that far, before it waits for the router
    if (this.paper != undefined) {
      this.paper.off();
      // this.paper.remove();
      this.graph.clear();
    }
    if (this.avoidRouter != undefined) {
      this.avoidRouter.destroy();
    }
  }

  override update(flowComponent: FlowViewHost): void {
    let appsGroups = flowComponent.groupsApps;

    let visualizationData = this.computeVisualizationData(this.graphData, appsGroups, [], false);
    let cellGenerationResult = this.generateCells(visualizationData, false);
    updateGraph(this.graph, cellGenerationResult.allCells);
    this.graph.getCells().map((c) => {
      let parentCellId = c.parent();
      if (parentCellId != undefined) {
        let parentCell = this.graph.getCell(parentCellId);
        this.fitParentNodeWithChildren(parentCell, this.graph);
      }
    });
    this.linksShouldBeUpdated = true;
    // this.avoidRouter.routeLinksOnly();

    if (this.routerAlgorithm == "libavoid") {
      this.avoidRouter.routeAllSoon();
    }
  }

  override isOutdated = () => {
    return this.flowComponent.selectedView != "apps-groups";
  }

  override zoomInVisualisation = (flowComponent: FlowViewHost) => {
    const oldScale = this.paper.scale().sx;
    const newScale = oldScale * 1.1;
    this.paper.scale(newScale, newScale);
  }

  override zoomOutVisualisation = (flowComponent: FlowViewHost) => {
    const oldScale = this.paper.scale().sx;
    const newScale = oldScale * 0.9;
    this.paper.scale(newScale, newScale);
  }

  override recenterVisualisation = (flowComponent: FlowViewHost) => {
    // this.camera.animatedReset({duration: 600});
    this.fitGraphToView();
  }

  /**
   * Scales the graph to fit the view port, centered in the part of it left free by the floating apps groups
   * panel: the paper spans the whole view, the panel lying over its left side when it is open.
   */
  fitGraphToView = () => {
    const paperRect = this.paper.el.getBoundingClientRect();

    let freeLeft = 0;
    const panelStack = this.paper.el.parentElement?.querySelector(".agp-stack");
    if (panelStack != undefined) {
      const panelRect = panelStack.getBoundingClientRect();
      if (panelRect.width > 0) {
        freeLeft = Math.max(0, panelRect.right - paperRect.left);
      }
    }
    // A view too narrow to leave room beside the panel is used whole, the graph lying under the panel
    if (paperRect.width - freeLeft < 200) {
      freeLeft = 0;
    }

    this.paper.transformToFitContent({
      padding: 40,
      minScale: 0.1,
      maxScale: 4,
      preserveAspectRatio: true,
      fittingBBox: {x: freeLeft, y: 0, width: paperRect.width - freeLeft, height: paperRect.height},
      horizontalAlign: "middle",
      verticalAlign: "middle",
    });
  }

  /**
   * Writes the whole graph to a file, whatever the user zoomed or panned to: an SVG or a PNG picture, or a
   * draw.io diagram. See AppsGroupsGraphExport, which draws them from the model rather than from the paper.
   */
  exportAs = async (format: AppsGroupsExportFormat) => {
    if (this.paper == undefined) {
      console.warn("[apps-groups] the graph was not drawn yet, there is nothing to export");
      return;
    }

    await new AppsGroupsGraphExport(this.graph, this.paper).export(format);
  }

  // Same file as 'exportAs', given back rather than downloaded
  exportToBlob = async (format: AppsGroupsExportFormat): Promise<Blob> => {
    if (this.paper == undefined) {
      return undefined;
    }
    return await new AppsGroupsGraphExport(this.graph, this.paper).toBlob(format);
  }

  override exportAsImage = async () => {
    await this.exportAs("svg");
  }
}

